// Boston's Fenway Park look (the registry's `looks`; boston.js, the Charles at blue hour, is the city's first), "Night game
// under the Citgo sign". A warm, clear September night in the late innings, from the top rows of the grandstand behind
// home plate, a little to the first-base side of it (about 35 ft over the field and 175 ft behind the plate), looking out
// to left-centre (bearing about 25 degrees, north-north-east): the field bright green under the lights, the Green Monster
// across left field with its hand-worked scoreboard, the two light towers that stand on it, and the Citgo sign glowing
// over Kenmore Square between them, 1,100 ft beyond the wall. Everything is projected through one camera (CAM below: a
// level lens shifted down, so verticals stay vertical), in feet on the field's own axes: x along the first-base line, y
// along the third-base line (which runs north up to the Monster at 310 ft), z up. The Monster is drawn at its real size
// for the seat (37 ft by 231 ft, 0.17 as seen); the Citgo sign, its towers and the board's numbers sit where photos from
// these seats put them (the sign about 60 % along the wall from the foul pole, the towers at 22 % and 92 %).
// Left to right: the left-field grandstand running out to the Fisk pole, its roof with the Lansdowne Street hot-dog sign
// (the city's billboard, where the real park has a big roof sign); the Monster with the Monster seats on its top, the
// Kenmore Square buildings behind it (the hotel, 660 Beacon Street under the Citgo sign, the new glass blocks over the
// Pike); the centre-field bleachers and the video board over them, the bullpens; the infield diamond in front, and the
// box seats down both lines. The nearest rows of the grandstand fill the bottom: a few fans up close, the rest of the
// crowd a texture.
// What the brief hoped for but this seat can't show: the Pesky Pole, the right-field grandstand and the Back Bay towers
// (the Prudential, 200 Clarendon) stand 60 degrees to the right of the view's centre, out of the frame; the Charles, the
// harbour and the Old State House are miles away. The Citgo sign's face is seen at an angle, so it reads taller than wide.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 empties the park: no players, no fans, the lights still on.
//
// The loop's timeline (seconds). The loop plays the eighth inning: Boston in the field (white), then New York (grey).
//   0-96      top of the 8th: a pitch every 8 to 12 s (balls, strikes, swings, fouls), the fielders set on each pitch,
//             the scorekeeper marks his card after each one, the board's ball-strike-out lamps follow the count
//   0-8       Boston's fielders jog out from their dugout as the loop begins (the sides changed over the wrap)
//   24-31     a foul pop-up drifts back into the first-base boxes: the kid stands on his seat with his glove up, the
//             first baseman gives chase, a fan there gets it
//   30-74     BIG: the wave starts in the boxes behind home, rolls along the third-base side, across the Monster seats,
//             through the bleachers and round again (two laps); the fans up close stand into it each time
//   60-66     a single to left; the left fielder comes in, picks it up and throws it to second
//   90-95     a fly to centre is caught for the third out; 96-114 the sides change (Boston off to the first-base
//             dugout, New York out of the third-base dugout), New York's pitcher takes his warm-up throws
//   96-102    the scoreboard operator slides New York's 0 into the eighth's slot by hand
//   100-146   BIG: the late-inning singalong: the crowd stands and sways with its arms up, phone lights come on all
//             round the park (build 100-112, peak 112-138, fade), the couple sway arm in arm, the board glows warm;
//             115-146 the outfielders loosen up, throwing to each other
//   150-222   bottom of the 8th: pitches again; a grounder to short is thrown to first (152); New York's reliever
//             warms up in the bullpen behind the low wall
//   166-172   a line drive hits the Monster, the left fielder plays the carom and throws to second; the runner holds
//   176-214   BIG: the home run: a foul and a ball (176-182) while the fans lean in, the pitch at 188.3, the ball arcs
//             high over left-centre, over the Monster seats and on past the Citgo sign; the crowd rises in a wave of
//             colour from the near seats outwards, the fans up close cheer, the batter and the runner round the bases
//             (189-209), the video board shows HOME RUN and the new score, and at 204-210 the operator puts Boston's 2
//             and 5 in their slots
//   210-223   a grounder to third, then a strikeout ends the inning; 226-244 the sides change back over the loop's
//             wrap while the operator clears the inning's plates (230-236)
//   all loop  the vendor comes along the walkway below us, calls out and sells the father a hot dog (every 60 s);
//             people walk the walkway with beers, lemonade, cotton candy and a boy on his father's shoulders (ten a
//             loop); the father explains the game to his kid; the couple share fried dough; moths circle the towers'
//             lamps and bats hawk them; a jet and a plane on the Logan approach cross the sky; thin cirrus drifts over
//             the few stars; windows in Kenmore switch on and off; the crowd shifts all over the stands; a soft camera
//             flash now and then
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const ACCENT = '#44bb11';                           // the Green Line's green (the city's accent)

/* ================= The camera ================= */
// The eye: 30 ft to the first-base side of the line through home and second, 175 ft behind the plate, 35 ft up. A level
// lens facing bearing 22 degrees off the third-base line, its centre shifted 4 degrees down (the horizon sits above the
// frame's middle). f in px. Field feet: x along the first-base line, y along the third-base line, z up.
const CAM = { ex: -30, ey: -175, h: 35, yaw: 22 * Math.PI / 180, f: 442 };
const HY = Math.round(135 - CAM.f * Math.tan(4 * Math.PI / 180));   // the horizon's row
const FW = [Math.sin(CAM.yaw), Math.cos(CAM.yaw)], RT = [Math.cos(CAM.yaw), -Math.sin(CAM.yaw)];
/** field point (ft) to screen: [x, y, depth ft] */
function P(x, y, z = 0) {
  const dx = x - CAM.ex, dy = y - CAM.ey, d = dx * FW[0] + dy * FW[1], s = dx * RT[0] + dy * RT[1];
  return [240 + CAM.f * s / d, HY + CAM.f * (CAM.h - z) / d, d];
}
/** the field point under screen pixel (sx, sy), below the horizon */
function groundAt(sx, sy) {
  const d = CAM.f * CAM.h / (sy - HY), s = (sx - 240) * d / CAM.f;
  return [CAM.ex + d * FW[0] + s * RT[0], CAM.ey + d * FW[1] + s * RT[1], d];
}
/** the ray in the field's plane for screen column sx: origin and direction (direction scaled to unit depth) */
const rayOf = sx => { const a = (sx - 240) / CAM.f; return [FW[0] + a * RT[0], FW[1] + a * RT[1]]; };
/** depth at which column sx's ray crosses segment a-b (ft), and how far along it (0..1), or null */
function cross(sx, a, b) {
  const [rx, ry] = rayOf(sx), ex = b[0] - a[0], ey = b[1] - a[1], qx = a[0] - CAM.ex, qy = a[1] - CAM.ey;
  const den = rx * ey - ry * ex;
  if (Math.abs(den) < 1e-9) return null;
  const d = (qx * ey - qy * ex) / den, u = (qx * ry - qy * rx) / den;
  return d > 0 && u >= 0 && u <= 1 ? { d, u } : null;
}
const rowAt = (z, d) => HY + CAM.f * (CAM.h - z) / d;   // the screen row of height z at depth d

/* ================= The park, in feet ================= */
const D2R = Math.PI / 180;
const MON_A = [0, 310], MON_B = [231 * Math.sin(88 * D2R), 310 + 231 * Math.cos(88 * D2R)];   // the Monster: 231 ft, 37 ft tall
const MON_LEN = 231, MON_H = 37;
const TRI = [420 * Math.sin(52 * D2R), 420 * Math.cos(52 * D2R)];                            // the triangle, 420 ft
// the outfield wall from the Fisk pole round to the Pesky Pole: [point, height of the wall that starts there]
const OUTWALL = [[MON_A, MON_H], [MON_B, 17], [TRI, 5], [[357, 130], 4], [[340, 60], 3], [[314, 22], 3], [[302, 0], 3]];
// the field-level walls in front of the box seats, down the third-base side from the backstop to the Fisk pole, and down
// the first-base side from the backstop to the Pesky Pole
const WALL3 = [[-42, -42], [-30, 0], [-26, 100], [-20, 220], [-8, 300], MON_A];
const WALL1 = [[-42, -42], [0, -30], [100, -26], [220, -20], [300, -8], [302, 0]];
const BASES = { home: [0, 0], first: [90, 0], second: [90, 90], third: [0, 90] };
const RUBBER = [42.8, 42.8], MOUND = [41.7, 41.7];  // the rubber at 60.5 ft, the mound's centre at 59 ft
const monAt = (u, back = 0) => [MON_A[0] + (MON_B[0] - MON_A[0]) * u / MON_LEN, MON_A[1] + (MON_B[1] - MON_A[1]) * u / MON_LEN + back];
// screen geometry the layers share
const [MON_X0, MON_T0] = P(...MON_A, MON_H), [MON_X1, MON_T1] = P(...MON_B, MON_H);
const MON_B1 = P(...MON_B, 0)[1];
const MON = { x0: Math.round(MON_X0), x1: Math.round(MON_X1), top: Math.round(Math.min(MON_T0, MON_T1)) };
// the light towers on the Monster (22 % and 92 % along, behind the seats): foot on the wall top, head 140 ft up
const TOWERS = [51, 212].map(u => {
  const [fx, fy] = monAt(u, 14), [x, top, d] = P(fx, fy, 140), [, foot] = P(fx, fy, MON_H + 6);
  return { x: Math.round(x), top: Math.round(top), foot: Math.round(foot), d, hw: Math.round(CAM.f * 20 / d), hh: Math.round(CAM.f * 11 / d) };
});
// the Citgo sign on 660 Beacon Street, 1,100 ft out, seen where photos from these seats put it (60 % along the wall);
// its face 60 ft square, its centre 145 ft over the field, seen at an angle (so narrower than tall)
const CIT = (() => {
  const cx = Math.round(lerp(MON.x0, MON.x1, 0.6)), d = 1300, h = Math.round(CAM.f * 60 / d);
  const cy = Math.round(rowAt(145, d));
  return { x: cx - 8, y: cy - 10, w: 17, h: 21, cx, cy, d, px: h };
})();
// the billboard: a Lansdowne Street hot-dog stand's sign on the Monster's top, between the left tower and the Citgo sign,
// where the real wall has its ad boards
const BB = { x: 148, y: 70, w: 46, h: 20 };

/* ================= Timeline ================= */
// the eighth inning: Boston in the field for the top (white), New York for the bottom (grey)
const BOTTOM = { t0: 148, t1: 226 };
const CHANGE = [{ t0: 96, t1: 114, off: 'bos', on: 'ny' }, { t0: 226, t1: 244, off: 'ny', on: 'bos' }];   // the second wraps
const PITCH_P = 12;                                 // a pitch every 12 s while the half-inning plays
const WAVE = { t0: 30, t1: 74, lap: 22 };           // the wave: two laps of 22 s
const SING = { t0: 100, peak: 112, fall: 138, t1: 146 };
const FOUL = { t: 24.6, land: 30.2 };               // the pop-up into the first-base boxes
const SINGLE = { t: 60.6 };                         // the single to left in the top half
const FLYOUT = { t: 90.6 };                         // the fly to centre that ends the top half
const CAROM = { t: 166.6 };                         // the line drive off the Monster
const HR = { build: 176, t: 188.3, land: 193.2, t1: 214 };   // the home run
const KO = { t: 222.6 };                            // the strikeout that ends the inning
const OPS = [{ t: 96.5 }, { t: 204.5 }, { t: 230.5 }];
const VENDOR_P = 60;                                // the vendor climbs the aisle every 60 s
// The people: about half of a cast of 16 up close by default, and the park full. render(t, { crowd }) shows those whose
// rank is under crowd; rank 0 (the players, the umpires, the vendor, the father and his kid) stays while anybody does.
// The stands thin with crowd below CROWD; crowd 0 empties the park.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      if (a < 255 && d[o + 3]) { const k = a / 255; d[o] = lerp(d[o], v[0], k); d[o + 1] = lerp(d[o + 1], v[1], k); d[o + 2] = lerp(d[o + 2], v[2], k); return; }
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0, w: 1, h: 1 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}
const blit = (o, a = 1) => { if (!o) return; ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
const inWin = (t, a, b) => t >= a && t < b;
/** piecewise-linear keys [[t, v], ...]: the value at t */
function along(keys, t) {
  if (t <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0]));
  }
  return keys[keys.length - 1][1];
}

/* ================= The sky: a clear September night, the park's light dome, the city's glow ================= */
// Deep blue overhead, paling into the glow the city and the park throw up: orange low over Kenmore and the Back Bay
// (to the right, east), blue-white over the towers' lamps. A few stars where the glare lets them through.
const SKY = ['#05071a', '#070a1f', '#0a0e26', '#0d122d', '#111734', '#151c3b', '#1a2242', '#202848', '#272f4f', '#2f3654',
  '#383d58', '#42455b', '#4c4c5c'];
const DOME = (x, y) => Math.exp(-(((x - 200) / 170) ** 2) - (((y - 40) / 70) ** 2));
const skyV = (x, y) => Math.pow(y / HY, 1.7) * 0.5 + 0.34 * DOME(x, y) + 0.18 * Math.exp(-(((x - 400) / 140) ** 2)) * smooth(40, HY, y)
  + 0.1 * smooth(60, HY, y);
function renderSky() {
  const p = pix(W, HY + 8);
  for (let y = 0; y < HY + 8; y++) for (let x = 0; x < W; x++) {
    let v = skyV(x, Math.min(y, HY)) + (vnoise(x / 60, y / 9, 3) - 0.5) * 0.03, col = pick(SKY, v, x, y);
    const warm = 0.35 * Math.exp(-(((x - 410) / 120) ** 2)) * smooth(50, HY, y) + 0.15 * smooth(70, HY, y);
    if (warm > 0.05) col = mixHex(col, '#5a3f3a', warm * 0.6);
    p.set(x, y, col);
  }
  return p.done();
}
const hazeAt = (x, y) => SKY[Math.min(SKY.length - 1, Math.round(clamp(skyV(x, Math.min(y, HY)), 0, 1) * (SKY.length - 1)))];
const STARS = [];
{
  const r = rng(77);
  while (STARS.length < 26) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 60);
    if (skyV(x, y) > 0.2 || (x < 124 && y < 46) || (x > 378 && y < 26)) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.3 + r() * r() * 0.6, c: r() < 0.3 ? '#ffe8c8' : '#dfe6ff' });
  }
}
/* thin high cloud, tileable (period W): faint streaks, lit from below by the park and the city */
function makeWisps(seed, h, banks, col, colLit, a) {
  const r = rng(seed), streaks = [];
  for (let b = 0; b < banks; b++) {
    const bx0 = r() * W, by = 4 + r() * (h - 8), n = 2 + Math.floor(r() * 3);
    for (let i = 0; i < n; i++) streaks.push({ x: bx0 + (r() - 0.5) * 50, y: by + (r() - 0.5) * 6, rx: 20 + r() * 40, ry: 1 + r() * 1.8 });
  }
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0, lo = 0;
    for (const s of streaks) {
      let dx = x - s.x; dx -= Math.round(dx / W) * W;
      const q = 1 - (dx / s.rx) ** 2 - ((y - s.y) / s.ry) ** 2;
      if (q > v) { v = q; lo = y > s.y ? 1 : 0; }
    }
    v *= 0.75 + 0.5 * vnoise(x / 9, y / 2, seed, Math.round(W / 9));
    if (v > 0.14 + bayer(x, y) * 0.5) p.set(x, y, lo && v > 0.4 ? colLit : col, a);
  }
  return p.done();
}

/* ================= Windows that switch on and off ================= */
const WINS = [];
function addTog(r, x, y, w, h, col) { WINS.push({ x, y, w, h, c: col, k: 3 + Math.floor(r() * 6), ph: r(), duty: 0.3 + r() * 0.45 }); }
const WARMW = ['#ffd890', '#f8c070', '#ffe6b0', '#f0b060'], COOLW = ['#e4ecf8', '#c8d8ec', '#d8e2f0'];

/* ================= Kenmore Square and the Fenway behind the park ================= */
// Behind the Monster: the Hotel Commonwealth's long front on Kenmore Square (left), 660 Beacon Street's brick under the
// Citgo sign, the new glass blocks over the Pike (right); behind the bleachers the Fenway's mid-rises on Boylston and
// Brookline Avenue. [x0, x1, roof row, ramp, window kind, lit share]
const BRICK = ['#140c10', '#1e1216', '#2a1a1c', '#382222', '#482c28'];
const STONE = ['#14141c', '#1c1c26', '#262632', '#32323e', '#40404c'];
const GLASS = ['#0c1220', '#121a2c', '#182438', '#203046', '#2a3c56'];
const SKYLINE = [
  [96, 120, 78, STONE, 'dots', 0.18], [118, 176, 82, BRICK, 'rows', 0.3], [170, 198, 79, STONE, 'dots', 0.16],
  [196, 222, 82, BRICK, 'rows', 0.22], [222, 246, 67, GLASS, 'floors', 0.22], [244, 266, 73, GLASS, 'floors', 0.24],
  [264, 292, 78, STONE, 'dots', 0.18], [288, 318, 81, BRICK, 'rows', 0.22], [314, 336, 70, GLASS, 'floors', 0.2],
  [418, 444, 74, STONE, 'dots', 0.18], [440, 470, 66, GLASS, 'floors', 0.22], [466, 480, 77, BRICK, 'rows', 0.22],
  [46, 66, 52, GLASS, 'floors', 0.16], [64, 90, 64, STONE, 'dots', 0.18], [0, 28, 60, BRICK, 'rows', 0.18],
];
function renderSkyline(r) {
  const p = pix(W, 104);
  for (const [x0, x1, top, ramp, kind, lit] of SKYLINE) {
    for (let x = x0; x < x1; x++) for (let y = top; y < 104; y++) {
      let v = 0.4 + (x === x0 ? 0.25 : 0) + (x === x1 - 1 ? -0.2 : 0) + (y === top ? 0.35 : 0) + (vnoise(x / 3, y / 4, x0) - 0.5) * 0.1;
      let col = pick(ramp, v, x, y, 0.6);
      const inner = x > x0 && x < x1 - 1 && y > top + 1;
      if (inner && kind === 'rows' && (y - top) % 3 === 2 && x % 2 === 0) { const q = r(); if (q < lit) col = WARMW[Math.floor(r() * 4)]; else if (q < lit + 0.22) addTog(r, x, y, 1, 1, WARMW[1]); }
      if (inner && kind === 'dots' && (y - top) % 2 === 0 && x % 2 === 1) { const q = r(); if (q < lit) col = WARMW[Math.floor(r() * 4)]; else if (q < lit + 0.22) addTog(r, x, y, 1, 1, WARMW[2]); }
      if (inner && kind === 'floors' && (y - top) % 2 === 0) { const q = hash(x >> 2, y, x0); if (q < lit) col = COOLW[(x + y) % 3]; else if (q < lit + 0.08 && x % 4 === 0) addTog(r, x, y, 3, 1, COOLW[0]); }
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.3));
    }
    if (kind === 'floors') for (let x = x0; x < x1; x++) p.set(x, top, mixHex('#8aa0c0', hazeAt(x, top), 0.3));
  }
  return trim(p);
}

/* ================= The Citgo sign ================= */
// Its LEDs at night: the white face, the red trimark, CITGO in blue, a pale blue edge; seen at an angle from here, so a
// little narrower than tall. It stands on a steel frame on 660 Beacon Street's roof.
const CIT_LET = { C: ['11', '10', '10', '11'], I: ['1', '1', '1', '1'], T: ['111', '010', '010', '010'], G: ['11', '10', '11', '11'], O: ['111', '101', '101', '111'] };
function renderCitgo() {
  const w = CIT.w, h = CIT.h, p = pix(w, h + 6);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
    p.set(x, y, edge ? (y === 0 || x === 0 ? '#a8bcff' : '#7a90e0') : (y < 4 ? '#fbfcff' : '#eef2ff'));
  }
  for (let j = 2; j <= 11; j++) {                    // the trimark
    const hw = (j - 2) * 0.66, cx = 8;
    for (let x = Math.round(cx - hw); x <= Math.round(cx + hw); x++) p.set(x, j, j < 7 ? '#f2343a' : '#dc1e26');
  }
  p.set(8, 2, '#ff6a6a');
  let lx = 1;                                        // the word
  for (const ch of 'CITGO') { CIT_LET[ch].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(lx + i, 13 + j, '#1c3cb4'); }); lx += CIT_LET[ch][0].length + 1; }
  for (let y = h; y < h + 6; y++) {                  // the frame under it
    for (const x of [2, 8, 14]) p.set(x, y, '#2a2c38');
    p.set(2 + ((y - h) * 2) % 12, y, '#22242e');
  }
  return p.done();
}

/* ================= The light towers on the Monster ================= */
// White lattice masts with X bracing, a catwalk under each head, and the head: a bank of lamps in rows, burning white.
function renderTower(T) {
  const { x, top, foot, hw, hh } = T, p = pix(W, H);
  const sTop = top + hh + 2, wTop = Math.max(5, Math.round(hw * 0.32)), wFoot = wTop + 2;
  for (let y = sTop; y <= foot; y++) {
    const u = (y - sTop) / (foot - sTop), half = Math.round(lerp(wTop, wFoot, u) / 2), xl = x - half, xr = x + half;
    p.set(xl, y, '#c8ccd4'); p.set(xr, y, '#8a8e9a');
    const k = (y - sTop) % 8, span = xr - xl;                                            // the X bracing
    if (span > 1) { p.set(xl + Math.round(span * k / 8), y, '#9a9eaa'); p.set(xr - Math.round(span * k / 8), y, '#7a7e8a'); }
    if (k === 0) for (let i = xl; i <= xr; i++) p.set(i, y, '#a8acb6');
  }
  for (let i = x - hw + 2; i <= x + hw - 2; i++) { p.set(i, sTop - 1, '#5a5e6a'); if (i % 3 === 0) p.set(i, sTop - 2, '#4a4e5a'); }   // the catwalk and its rail
  for (let y = top; y < top + hh; y++) for (let i = x - hw; i <= x + hw; i++) {           // the head
    const fr = y === top || y === top + hh - 1 || i === x - hw || i === x + hw;
    let col = fr ? '#3a3e4a' : '#1a1c26';
    if (!fr && (y - top) % 2 === 1 && (i - x + hw) % 2 === 1) col = hash(i, y, 9) < 0.08 ? '#d8dcff' : hash(i, y, 8) < 0.5 ? '#ffffff' : '#fff8e6';
    else if (!fr && hash(i, y, 7) < 0.35) col = '#8a8ea0';
    p.set(i, y, col);
  }
  return trim(p);
}

/* ================= The Monster's face ================= */
// Fenway green, the scoreboard in its lower part (worked by hand from inside: white plates on green, the line score
// for New York and Boston, ball-strike-out lamps, other games' scores), the ladder, 310 at the foul line and 379 in
// left-centre, a couple of invented ads either side of the board. Drawn per column from the camera: u is the feet
// along the wall from the foul pole, z the height on it.
const MONG = ['#0e2a1a', '#123222', '#163a28', '#1b442f', '#215036', '#285c3e'];
const PLATE = '#f2f0e6', BOARD = '#0f2a1c', FRAME = '#0a1a10', SLOT = '#050e08', PLATE_BG = '#173e27';
// the board's plates: the line score (innings 1-9, runs, hits), drawn per frame where they change
const LINE = { ny: ['0', '1', '0', '0', '2', '0', '0', '', '', '3', '7'], bos: ['1', '0', '0', '2', '0', '0', '0', '', '', '3', '6'] };
// The panels on the wall's lower half: one level row (from this seat the wall's top edge runs level, so its boards are
// drawn level too, not stepped down the wall's slanting foot), green wall between them. Each panel's frame rows and its
// left and right frame columns in screen px; each word centred in its panel, two px clear of the frame. The wide one is
// the line score; the ladder runs up the gap left of it.
const PANEL = { t: 116, b: 130 };
const PANELS = [{ x0: 103, x1: 135, word: 'HUB CAB' }, { x0: 139, x1: 171, word: 'LOBSTAH' }, { x0: 175, x1: 243 },
  { x0: 247, x1: 279, word: 'SEAPORT' }];
const LADDER_X = 173;
// the line score, in screen px: the teams in one column, nine inning slots four apart (a 3x5 plate each, dark where no
// plate hangs yet), a rule, the runs, a rule, the ball-strike-out lamps
const LS = (() => {
  const x = PANELS[2].x0 + 3;
  return { team: x, inn: x + 14, rules: [x + 50, x + 56], runs: x + 52, lamps: x + 58, rows: { ny: PANEL.t + 2, bos: PANEL.t + 8 } };
})();
const slotXY = (team, col) => [col === 'runs' ? LS.runs : LS.inn + col * 4, LS.rows[team]];
const monCol = sx => { const c = cross(sx, MON_A, MON_B); return c ? { d: c.d, u: c.u * MON_LEN } : null; };
/** screen point on the Monster's face at u ft along, z ft up */
const monP = (u, z) => { const [x, y] = monAt(u); return P(x, y, z); };

/* ================= The stands and the crowd ================= */
// Every seat is a point in the park's feet: rows stepping back and up behind the field-level walls, the bleachers
// behind the centre-field wall, three rows and a standing rail on the Monster. A fan is drawn at the seat's projected
// scale (px a foot): a head and a shirt, from behind in the boxes, facing us in the bleachers and on the Monster. The
// crowd is a texture: prerendered seated and standing, at three densities (the crowd knob); the wave, the home run and
// the singalong draw the standing fans over the seated ones.
const SHIRTS = ['#c8202a', '#c8202a', '#b01c26', '#1c2a52', '#22305e', '#e8e6de', '#d8d6ce', '#8a8c92', '#2a2a30', '#2e5a3a', '#d8b030',
  '#c86a8a', '#3a6aa8', '#e8e6de', '#c8202a', '#6a3a2a'];
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#c8a058', '#8a3a1c', '#a8a4a0'];
const CAPS = ['#1c2a52', '#c8202a', '#1c2a52', '#2a2a30'];
const SEC = {};                                     // section id -> { fans: [...], lap0, lap1 }
let LAP_LEN = 0;
/** build the seats of one section: rows o ft back from a wall polyline (normal away from the field), z = z0 + o * slope */
function seatRows(id, wall, { o0 = 6, o1 = 60, dO = 3, z0 = 2, slope = 0.32, face = false, dim = 0.3, sOff = 0, roof = null, seed = 1, dMin = 20 }) {
  // the outward normal of each segment (away from home), and mitred normals at the vertices, so the rows stay whole
  const n = wall.length, segN = [];
  let len = 0;
  for (let i = 1; i < n; i++) {
    const a = wall[i - 1], b = wall[i], l = Math.hypot(b[0] - a[0], b[1] - a[1]), mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
    let nx = (b[1] - a[1]) / l, ny = -(b[0] - a[0]) / l;
    if ((mx + nx) ** 2 + (my + ny) ** 2 < mx * mx + my * my) { nx = -nx; ny = -ny; }
    segN.push([nx, ny]); len += l;
  }
  const vN = wall.map((_, i) => {
    const A = segN[Math.max(0, i - 1)], B = segN[Math.min(segN.length - 1, i)], mx = A[0] + B[0], my = A[1] + B[1], ml = Math.hypot(mx, my);
    const k = 1 / Math.max(0.5, (A[0] * mx + A[1] * my) / ml);
    return [mx / ml * k, my / ml * k];
  });
  const fans = [];
  for (let o = o0; o <= o1; o += dO) {
    const z = z0 + o * slope, under = roof && o > roof.o, row = wall.map((v, i) => [v[0] + vN[i][0] * o, v[1] + vN[i][1] * o]);
    let rl = 0; const rs = [];
    for (let i = 1; i < n; i++) { const l = Math.hypot(row[i][0] - row[i - 1][0], row[i][1] - row[i - 1][1]); rs.push(l); rl += l; }
    for (let q = 1, i = 0, acc = 0; q < rl; q += 2) {
      while (i < rs.length - 1 && q > acc + rs[i]) { acc += rs[i]; i++; }
      const u = clamp((q - acc) / rs[i], 0, 1), x3 = lerp(row[i][0], row[i + 1][0], u), y3 = lerp(row[i][1], row[i + 1][1], u);
      const s = q / rl * len, [sx, sy, d] = P(x3, y3, z);
      if (sx < -4 || sx > W + 4 || sy < 0 || sy > 236 || d < dMin) continue;
      const S = Math.round(s * 7), hsh = hash(S, o, seed), k = Math.floor(hash(S, o, seed + 1) * SHIRTS.length), dd = dim + (under ? 0.14 : 0);
      fans.push({ sx, sy, d, sc: CAM.f / d, s, o, z, empty: hsh > 0.88 || Math.round(s) % 44 < 3, rank: hash(S, o, seed + 2),
        shirt: mixHex(SHIRTS[k], '#0a0c1a', dd + clamp((d - 200) / 900, 0, 0.35)),
        skin: mixHex(SKINS[Math.floor(hash(S, o, seed + 3) * 4)], '#0a0c1a', dd + 0.1),
        hair: mixHex(hash(S, o, seed + 4) < 0.3 ? CAPS[Math.floor(hash(S, o, seed + 5) * 4)] : HAIRS[Math.floor(hash(S, o, seed + 6) * 6)], '#0a0c1a', dd),
        face, lap: sOff + s, under, ph: hash(S, o, seed + 7) });
    }
  }
  fans.sort((a, b) => b.d - a.d);
  SEC[id] = { fans, len, lap0: sOff, lap1: sOff + len };
  return len;
}
/** one fan through a rect painter put(x, y, w, h, col): seated, standing, arms up; lean: px to the side */
function fanDraw(put, f, stand = 0, arms = 0, lean = 0) {
  const s = f.sc, x = Math.round(f.sx + lean), y = Math.round(f.sy);
  if (s < 1.25) {                                   // a fleck: a head over a shirt
    const hy = y - (stand ? 3 : 2);
    put(x, hy, 1, 1, f.face && f.ph < 0.35 ? f.skin : f.ph < 0.6 ? f.hair : f.shirt); put(x, hy + 1, 1, stand ? 2 : 1, f.shirt);
    if (arms) put(f.ph < 0.5 ? x - 1 : x + 1, hy - 1, 1, 1, f.ph < 0.25 || f.ph > 0.75 ? f.skin : f.shirt);
    return;
  }
  const hs = Math.max(1, Math.round(0.72 * s)), tw = Math.max(2, Math.round(1.5 * s)), th = Math.max(1, Math.round(1.25 * s));
  const hy = y - Math.round((stand ? 5.6 : 4.2) * s), hx = x - (hs >> 1), tx = x - (tw >> 1);
  put(tx, hy + hs, tw, th, f.shirt);
  put(hx, hy, hs, hs, f.face ? f.skin : f.hair);
  if (f.face && hs > 1) put(hx, hy, hs, 1, f.hair);
  if (arms) {
    const ah = Math.max(2, Math.round(1.1 * s));
    put(tx - 1, hy - ah + hs, 1, ah + 1, f.shirt); put(tx + tw, hy - ah + hs, 1, ah + 1, f.shirt);
    put(tx - 1, hy - ah + hs - 1, 1, 1, f.skin); put(tx + tw, hy - ah + hs - 1, 1, 1, f.skin);
  }
}
const LEVELS = [1, 0.5, 0.25];                       // the densities the stands are prerendered at
/** the crowd layer of a section at a density: seated or standing, arms up or not */
function renderCrowd(id, level, stand, arms) {
  const p = pix(W, H), put = (x, y, w, h, col) => p.fill(x, y, w, h, col);
  for (const f of SEC[id].fans) if (!f.empty && f.rank < level) fanDraw(put, f, stand, arms);
  return trim(p);
}
/** the stands without anyone: rows of seats, the steps of the aisles */
function renderSeats(id, seatCol, stepCol) {
  const p = pix(W, H);
  for (const f of SEC[id].fans) {
    const s = f.sc, x = Math.round(f.sx), y = Math.round(f.sy), w = Math.max(1, Math.round(1.8 * s));
    const aisle = Math.round(f.s) % 44 < 3;
    if (s < 1.25) { p.set(x, y - 1, aisle ? stepCol : seatCol); p.set(x, y, mixHex(seatCol, '#000000', 0.4)); continue; }
    const sb = Math.max(1, Math.round(0.9 * s));
    p.fill(x - (w >> 1), y - Math.round(2.6 * s), w, sb, aisle ? stepCol : f.under ? mixHex(seatCol, '#000000', 0.4) : seatCol);
    p.fill(x - (w >> 1), y - Math.round(2.6 * s) + sb, w, Math.max(1, Math.round(1.6 * s)), aisle ? stepCol : '#0c1210');
  }
  return trim(p);
}

/* ================= The field ================= */
// Painted per pixel from the camera: the outfield mowed in a checkerboard along the foul lines, the infield's skinned
// arc 95 ft round the rubber, its grass square, the base paths, the mound and the plate's circle, the warning track
// round the walls; then the chalk, the bases and the plate drawn as projected lines. Bright under the lights, a little
// hazier far off.
const FIELD = [[-42, -42], ...WALL1.slice(1), ...OUTWALL.slice(0, -1).map(w => w[0]).reverse(), ...WALL3.slice(1, -1).reverse()];
function inPoly(x, y, poly) {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) c = !c;
  }
  return c;
}
function distSeg(px, py, a, b) {
  const ex = b[0] - a[0], ey = b[1] - a[1], t = clamp(((px - a[0]) * ex + (py - a[1]) * ey) / (ex * ex + ey * ey), 0, 1);
  return Math.hypot(px - a[0] - ex * t, py - a[1] - ey * t);
}
const GRASS_L = ['#2a6e2e', '#317a34', '#38863a', '#40923f'], GRASS_D = ['#24622a', '#2a6c2f', '#307634', '#378239'];
const DIRT = ['#7e4c2e', '#935a36', '#a8693e', '#b87848', '#c68752'], TRACK = ['#5a3826', '#68422c', '#764c32', '#845638'];
/** the kind of ground at a field point: 'out', 'track', 'dirt', 'grass', 'igrass', 'mound' */
const BULLPEN = [TRI, [357, 130], [398, 150], [395, 165], [370, 210], [355, 232], [340, 250]];
const PEN_MOUNDS = [[372, 176], [356, 210]];
function groundKind(x, y) {
  if (!inPoly(x, y, FIELD)) return inPoly(x, y, BULLPEN) ? (PEN_MOUNDS.some(m => Math.hypot(x - m[0], y - m[1]) < 5) ? 'mound' : 'pen') : 'out';
  let dw = 1e9;
  for (let i = 1; i < FIELD.length + 1; i++) dw = Math.min(dw, distSeg(x, y, FIELD[i - 1], FIELD[i % FIELD.length]));
  if (dw < 12) return 'track';
  const dh = Math.hypot(x, y), dm = Math.hypot(x - MOUND[0], y - MOUND[1]);
  if (dm < 9) return 'mound';
  if (dh < 13) return 'dirt';
  if ((Math.abs(y) < 3 && x > 0 && x < 92) || (Math.abs(x) < 3 && y > 0 && y < 92)) return 'dirt';
  const inSq = x > 3 && y > 3 && x < 87 && y < 87 && Math.hypot(x - 90, y) > 13 && Math.hypot(x - 90, y - 90) > 13 && Math.hypot(x, y - 90) > 13;
  if (inSq) return 'igrass';
  if (Math.hypot(x - RUBBER[0], y - RUBBER[1]) < 95 && x > -5 && y > -5) return 'dirt';
  return 'grass';
}
function renderField() {
  const p = pix(W, H);
  for (let sy = HY + 2; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
    const [x, y, d] = groundAt(sx, sy), k = groundKind(x, y);
    if (k === 'out') continue;
    const haze = clamp((d - 250) / 700, 0, 0.3), n = (vnoise(x / 9, y / 9, 41) - 0.5) * 0.14 + (hash(sx, sy, 42) - 0.5) * 0.06;
    const lit = 0.55 + 0.12 * Math.exp(-(((x - 120) / 260) ** 2) - (((y - 200) / 260) ** 2));
    let col;
    if (k === 'grass' || k === 'igrass') {
      const sq = k === 'grass' ? 30 : 15, chk = (Math.floor(x / sq) + Math.floor(y / sq)) & 1;
      col = pick(chk ? GRASS_L : GRASS_D, lit + n, sx, sy, 0.6);
    } else if (k === 'track') col = pick(TRACK, 0.5 + n * 1.5, sx, sy, 0.7);
    else if (k === 'pen') col = pick(GRASS_D, 0.5 + n, sx, sy, 0.6);
    else if (k === 'mound') col = pick(DIRT, 0.6 + n - Math.min(Math.hypot(x - MOUND[0], y - MOUND[1]), 9) * 0.02, sx, sy, 0.7);
    else col = pick(DIRT, 0.5 + n * 1.4, sx, sy, 0.7);
    p.set(sx, sy, mixHex(col, '#1e3236', haze));
  }
  // the chalk: the foul lines out to the walls, the batter's and catcher's boxes, the plate, the bases, the rubber
  const L = (a, b, col = '#e6eadc') => { const A = P(...a), B = P(...b); p.line(Math.round(A[0]), Math.round(A[1]), Math.round(B[0]), Math.round(B[1]), col); };
  L([0, 3], [0, 300]); L([3, 0], [296, 0]);
  for (const sgn of [1, -1]) { const ox = sgn * 3.2; L([ox + 1.5, -2 + ox], [ox + 1.5 + 4, -2 + ox + 4], '#d8dccf'); }
  for (const [bx, by] of [BASES.first, BASES.second, BASES.third]) { const [x, y] = P(bx, by); p.set(Math.round(x), Math.round(y), '#ffffff'); p.set(Math.round(x) + 1, Math.round(y), '#e8ece4'); }
  const [hx, hy] = P(0, 0); p.set(Math.round(hx), Math.round(hy), '#ffffff'); p.set(Math.round(hx) - 1, Math.round(hy), '#f0f0e8'); p.set(Math.round(hx) + 1, Math.round(hy), '#f0f0e8');
  const [rx, ry] = P(...RUBBER); p.set(Math.round(rx), Math.round(ry), '#ffffff');
  return trim(p);
}

/* ================= The walls ================= */
// The Monster per column (its scoreboard, ladder, numbers and ads), the centre-field wall and the bullpens' low wall
// with the yellow line along their tops, and the padded walls in front of the box seats.
function renderWalls() {
  const p = pix(W, H);
  // the warning track's dirt along the outfield walls' foot: one row, all the way along (the field alone shows it only
  // in dashes, where the foot's slope steps)
  const trackRow = (sx, y, d) => {
    const n = (vnoise(sx / 9, 3, 41) - 0.5) * 0.14 + (hash(sx, y, 42) - 0.5) * 0.06;
    p.set(sx, y, mixHex(pick(TRACK, 0.5 + n * 1.5, sx, y, 0.7), '#1e3236', clamp((d - 250) / 700, 0, 0.3)));
  };
  for (let sx = 0; sx < W; sx++) {
    const m = monCol(sx);
    if (m) {
      const { d, u } = m, yt = Math.round(rowAt(MON_H, d)), yb = Math.round(rowAt(0, d)), pn = PANELS.find(q => sx >= q.x0 && sx <= q.x1);
      for (let y = yt; y <= yb; y++) {
        const z = MON_H * (yb - y) / (yb - yt);
        let v = 0.55 + (z > 36 ? 0.3 : 0) + (z < 1.2 ? -0.25 : 0) + (u % 15 < 0.5 ? -0.12 : 0) + (vnoise(u / 6, z / 3, 51) - 0.5) * 0.08 - (1 - z / MON_H) * 0.08;
        let col = pick(MONG, v, sx, y, 0.6);
        if (pn && y >= PANEL.t && y <= PANEL.b) {                                            // a panel: its frame, its face
          const fr = sx === pn.x0 || sx === pn.x1 || y === PANEL.t || y === PANEL.b;
          col = fr ? FRAME : pick([BOARD, '#13321f', '#173a24'], 0.4 + (vnoise(u / 4, y / 2, 52) - 0.5) * 0.3, sx, y, 0.6);
        }
        if (sx === LADDER_X && y > yt + 1 && y < PANEL.t) col = (y & 1) ? '#5a6a5a' : '#2a3a2e';   // the ladder
        p.set(sx, y, col);
      }
      trackRow(sx, yb + 1, d);
      continue;
    }
    for (const [a, b, h, base] of [[MON_B, TRI, 17, '#163a28'], [TRI, OUTWALL[3][0], 5, '#1a4430'], [OUTWALL[3][0], OUTWALL[4][0], 4, '#1a4430']]) {
      const c = cross(sx, a, b);
      if (!c) continue;
      const yt = Math.round(rowAt(h, c.d)), yb = Math.round(rowAt(0, c.d));
      for (let y = yt; y <= yb; y++) p.set(sx, y, y === yt ? '#d8b830' : pick(MONG, 0.45 + (y === yt + 1 ? 0.2 : 0) - (y - yt) * 0.02, sx, y, 0.6));
      trackRow(sx, yb + 1, c.d);
      break;
    }
  }
  // the padded walls in front of the boxes, down both lines
  for (const wall of [WALL3, WALL1]) for (let i = 1; i < wall.length; i++) for (let s = 0; s <= 1; s += 0.004) {
    const x3 = lerp(wall[i - 1][0], wall[i][0], s), y3 = lerp(wall[i - 1][1], wall[i][1], s), [sx, yt] = P(x3, y3, 4), [, yb] = P(x3, y3, 0);
    for (let y = Math.round(yt); y <= Math.round(yb); y++) p.set(sx, y, y === Math.round(yt) ? '#3a6a4a' : '#163a28');
  }
  // the Fisk pole, 95 ft up from the corner, its screen on the fair side
  const [px0, pyb] = P(...MON_A, 0), [, pyt] = P(...MON_A, 95), [, pys] = P(...MON_A, 70);
  for (let y = Math.round(pyt); y <= Math.round(pyb); y++) {
    p.set(Math.round(px0), y, '#f0d040'); p.set(Math.round(px0) - 1, y, y < MON_T0 ? '#a88a20' : '#c8a828');
    if (y < pys && y > pyt + 2) p.set(Math.round(px0) + 1, y, (y & 1) ? '#8a7a3a' : '#5a5030');
  }
  // the numbers: 310 by the foul pole over the panels' row, each ad's word centred in its panel
  const set = (x, y, col) => p.set(x, y, col);
  const [x310] = monP(5, 20); stamp(set, '310', Math.round(x310) - 3, PANEL.t - 7, '#e8e6da');
  const textY = PANEL.t + 1 + ((PANEL.b - PANEL.t - 1 - 5) >> 1);
  for (const { x0, x1, word } of PANELS) if (word) stamp(set, word, x0 + 1 + ((x1 - x0 - 1 - textW(word)) >> 1), textY, '#e4e2d4');
  // the line score's fixed parts: the team names, the rules, and every slot's dark hole (the plates hang in per frame)
  for (const team of ['ny', 'bos']) {
    const y = LS.rows[team];
    stamp(set, team === 'ny' ? 'NY' : 'BOS', LS.team, y, '#d8d8cc');
    for (let i = 0; i < 9; i++) p.fill(LS.inn + i * 4, y, 3, 5, SLOT);
    p.fill(LS.runs, y, 3, 5, SLOT);
  }
  for (const x of LS.rules) p.fill(x, PANEL.t + 1, 1, PANEL.b - PANEL.t - 1, FRAME);
  return trim(p);
}
// the bleachers' front wall behind the bullpens, 17 ft, padded green, round to the triangle where the centre-field wall
// takes over (no gap left at the corner)
function renderBleachFront() {
  const p = pix(W, H), wall = [...BLEACH_WALL.slice(0, 4), TRI];
  for (let sx = 0; sx < W; sx++) for (let i = 1; i < wall.length; i++) {
    const c = cross(sx, wall[i - 1], wall[i]);
    if (!c) continue;
    const yt = Math.round(rowAt(17, c.d)), yb = Math.round(rowAt(0, c.d));
    for (let y = yt; y <= yb; y++) p.set(sx, y, y === yt ? '#3a6a4a' : pick(MONG, 0.4 - (y - yt) * 0.015 + (Math.round(c.u * 40) % 5 === 0 ? -0.1 : 0), sx, y, 0.6));
    break;
  }
  return trim(p);
}
// the 3x5 letters the park's own signs spell, written out (they are baked once)
const FONT = {
  0: ['111', '101', '101', '101', '111'], 1: ['010', '110', '010', '010', '111'], 2: ['111', '001', '111', '100', '111'],
  3: ['111', '001', '011', '001', '111'], 4: ['101', '101', '111', '001', '001'], 5: ['111', '100', '111', '001', '111'],
  6: ['111', '100', '111', '101', '111'], 7: ['111', '001', '010', '010', '010'], 8: ['111', '101', '111', '101', '111'],
  9: ['111', '101', '111', '001', '111'], A: ['010', '101', '111', '101', '101'], B: ['110', '101', '110', '101', '110'],
  C: ['011', '100', '100', '100', '011'], D: ['110', '101', '101', '101', '110'], E: ['111', '100', '110', '100', '111'],
  G: ['011', '100', '101', '101', '011'], H: ['101', '101', '111', '101', '101'], I: ['111', '010', '010', '010', '111'],
  L: ['100', '100', '100', '100', '111'], M: ['101', '111', '111', '101', '101'], N: ['110', '101', '101', '101', '101'],
  O: ['010', '101', '101', '101', '010'], P: ['110', '101', '110', '100', '100'], R: ['110', '101', '110', '101', '101'],
  S: ['011', '100', '010', '001', '110'], T: ['111', '010', '010', '010', '010'], U: ['101', '101', '101', '101', '111'],
  Y: ['101', '101', '010', '010', '010'], ' ': ['000', '000', '000', '000', '000'], "'": ['010', '010', '000', '000', '000'],
};
// M, N and W drawn wider, so none of them reads as another letter (a 3-wide N is a Π, an M an H)
const W5 = ['10001', '10001', '10101', '10101', '01010'];
const WIDE = { M: ['10001', '11011', '10101', '10001', '10001'], N: ['1001', '1101', '1011', '1001', '1001'], W: W5 };
const glyphOf = ch => WIDE[ch] ?? FONT[ch];
/** a word's width in px, a column between letters */
const textW = s => [...s].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - 1;
/** write `s` with its top-left at x, y through set(x, y, col) */
function stamp(set, s, x, y, col) {
  for (const ch of s) {
    const g = glyphOf(ch);
    if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') set(x + i, y + j, col); });
    x += (g?.[0].length ?? 3) + 1;
  }
}

/* ================= The left-field grandstand's roof, the bleachers' back, the Monster's top ================= */
// The grandstand's roof over the back rows (we sit below its height, so its dark underside shows over them), the old
// steel columns holding it, its green fascia; the hot-dog sign stands on it. The bleachers end in a low back wall and the
// video board stands over them. On the Monster's top the seats' rail, posts and two invented ads between the towers.
const LF_ROOF = { o: 46, z: 58, oBack: 104, zBack: 66 };
function wallPt(wall, s) {                         // a point s ft along a polyline, with its outward normal
  let acc = 0;
  for (let i = 1; i < wall.length; i++) {
    const a = wall[i - 1], b = wall[i], l = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (s <= acc + l || i === wall.length - 1) {
      const u = clamp((s - acc) / l, 0, 1), x = lerp(a[0], b[0], u), y = lerp(a[1], b[1], u);
      let nx = b[1] - a[1], ny = -(b[0] - a[0]); const nl = Math.hypot(nx, ny); nx /= nl; ny /= nl;
      if ((x + nx) ** 2 + (y + ny) ** 2 < x * x + y * y) { nx = -nx; ny = -ny; }
      return [x, y, nx, ny];
    }
    acc += l;
  }
  return null;
}
const LF_WALL = [[-8, 300], [-20, 220], [-26, 100], [-30, 0]];
function renderLFRoof() {
  const back = pix(W, H), front = pix(W, H), edge = new Float32Array(W).fill(NaN), cols = [];
  for (let s = 0; s < 300; s += 0.25) {
    const [x, y, nx, ny] = wallPt(LF_WALL, s), [fx, fy] = P(x + nx * LF_ROOF.o, y + ny * LF_ROOF.o, LF_ROOF.z), X = Math.round(fx);
    if (X >= 0 && X < W && (isNaN(edge[X]) || fy < edge[X])) edge[X] = fy;
    if (Math.abs(s % 40) < 0.01 && s > 20) cols.push([x + nx * (LF_ROOF.o - 2), y + ny * (LF_ROOF.o - 2)]);
  }
  for (let X = 0; X < W; X++) {
    if (isNaN(edge[X])) continue;
    const y0 = Math.round(edge[X]);
    for (let yy = y0 - 3; yy < y0; yy++) front.set(X, yy, yy === y0 - 3 ? '#3e6e50' : '#1c4630');            // the fascia
    for (let yy = y0; yy < y0 + 40; yy++) back.set(X, yy, pick(['#06080a', '#0a0e12', '#10161a'], 0.7 - (yy - y0) * 0.02, X, yy));   // the underside
    if (X % 11 === 4) { front.set(X, y0 + 2, '#ffe6b0'); front.set(X, y0 + 1, '#6a5a40'); }   // its lamps
  }
  for (const [cx3, cy3] of cols) {                                                              // the roof's columns, down to the seats
    const [cx, cyT] = P(cx3, cy3, LF_ROOF.z), [, cyB] = P(cx3, cy3, 2 + (LF_ROOF.o - 2) * 0.4);
    if (cx < 0 || cx > W) continue;
    for (let yy = Math.round(cyT); yy <= Math.round(cyB); yy++) front.set(Math.round(cx), yy, '#1a3a2a');
  }
  return { back: trim(back), front: trim(front) };
}
// the bleachers' back wall and the light fixtures on it, behind the top rows
const BLEACH_WALL = [[395, 165], [370, 210], [355, 232], [340, 250], [322, 264], [300, 278], MON_B];
function renderBleachBack() {
  const p = pix(W, H);
  for (let s = 0; s < 330; s += 0.5) {
    const q = wallPt(BLEACH_WALL, s);
    if (!q) continue;
    const [x, y, nx, ny] = q, o = 124, [bx, bt] = P(x + nx * o, y + ny * o, 17 + o * 0.33 + 6), [, bb] = P(x + nx * o, y + ny * o, 17 + o * 0.33 - 2);
    if (bx < MON.x1 - 6 || bx > W) continue;
    for (let yy = Math.round(bt); yy <= Math.round(bb); yy++) p.set(Math.round(bx), yy, yy === Math.round(bt) ? '#3a5a48' : '#14261c');
  }
  return trim(p);
}
// the video board over the bleachers: its frame, drawn per frame inside (what it shows)
const VB = (() => {
  const [x0, y0] = P(342, 372, 102), [x1, y1] = P(420, 300, 72);
  return { x: Math.round(x0), y: Math.round(y0), w: Math.round(x1 - x0), h: Math.round(y1 - y0) };
})();
function renderBoardFrame() {
  const p = pix(W, H), { x, y, w, h } = VB;
  for (let j = -2; j <= h + 1; j++) for (let i = -2; i <= w + 1; i++) {
    const fr = i < 0 || j < 0 || i >= w || j >= h;
    if (fr) p.set(x + i, y + j, j === -2 ? '#3a4a44' : '#141c1a');
  }
  for (const lx of [x + 8, x + w - 9]) for (let yy = y + h + 2; yy < y + h + 14; yy++) { p.set(lx, yy, '#1a2420'); p.set(lx + 1, yy, '#10161a'); }   // its legs
  return trim(p);
}
// the Monster's top: the rail along the seats' front, the posts at the back and two invented ads between the towers
const MON_ADS = [[156, 203, 'CANNOLI']];          // wide and tall enough for its word to clear the frame by two px
function renderMonTop() {
  const p = pix(W, H);
  for (let u = 0; u <= MON_LEN; u += 0.25) {
    const [x, y] = monAt(u), [sx, sy] = P(x, y, MON_H + 3.5);
    p.set(Math.round(sx), Math.round(sy), '#5a7a64');
    const [, by] = P(x, y + 20, MON_H + 8);                                                      // the back rail of the standing room
    p.set(Math.round(sx), Math.round(by), '#2a4232');
  }
  for (const [a, b, word] of MON_ADS) {
    const [xa, ya] = monAt(a, 22), [xb, yb] = monAt(b, 22), [sa, ta] = P(xa, ya, 61), [sb] = P(xb, yb, 61), [, ba] = P(xa, ya, 48.5);
    const x0 = Math.round(sa), x1 = Math.round(sb), y0 = Math.round(ta), y1 = Math.round(ba);
    for (let yy = y0; yy <= y1; yy++) for (let xx = x0; xx <= x1; xx++) p.set(xx, yy, yy === y0 ? '#2e5a40' : (xx === x0 || xx === x1 || yy === y1) ? '#0a1c12' : '#143a24');
    for (const lx of [x0 + 4, x1 - 4]) for (let yy = y1 + 1; yy < y1 + 7; yy++) p.set(lx, yy, '#16241c');
    stamp((x, y, col) => p.set(x, y, col), word, Math.round((x0 + x1) / 2 - textW(word) / 2), Math.round((y0 + y1) / 2 - 2), '#a8b8a4');
  }
  return trim(p);
}

/* ================= People up close ================= */
// One function draws everyone near (after rome.js and boston.js): a look gives the proportions for a height and the
// colours dimmed for the night; person() puts it on its feet. September at a night game: T-shirts and jerseys without
// names, a hoodie, a light jacket, ball caps. Seen from behind, their hair and shoulders catch the field's white light.
const NIGHT = '#0a0e22', GLOWC = '#eef4e6';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.26, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.35), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.3), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: o.coat || 0, shorts: o.shorts || 0,
    hat: o.hat ? m(o.hat) : null, dress: o.dress || 0 };
}
/**
 * One person, feet (or seat) on row fy, centred on column x; returns the head's top row.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (phone, food), 3 out, 4 up and out (pointing), 5 round a
 * neighbour's back; o.lean: the head towards a neighbour; o.sit: seated
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1, low = P.shorts ? Math.ceil(legs * 0.55) : 0, legC = P.dress ? P.top : P.bot;
    ctx.fillStyle = 'rgba(4,6,10,0.4)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1) - 4, fy + 2, bw + 8, 1);
    if (side) {
      if (o.step) { R(x - 2, ly, 4, 2, legC); R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); }
      else R(x - 1, ly, 2, legs, legC);
      if (low) R(x - 1, fy - low + 1, 2, low - 1, P.skin);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, legC); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      if (low) { R(tx, fy - low + 1, lw, low - 1, P.skin); R(tx + bw - lw, fy - low + 1, lw, low - 1, P.skin); }
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  }
  R(tx, ty, bw, th + P.coat, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD); else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                                // the field's light on the shoulders
  const skinArm = !P.coat;
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else { R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD); }
  };
  if (side) {
    const st = o.aR || 0;
    if (!st) { R(x, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(x, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.topD); R(x + dir * 4, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); }
  return hy;
}
const tyOf = (P, fy, sit) => fy - (sit ? 0 : P.ll) - P.th + 1;
// the near row: its seat backs' top row, the row its people sit on; our own row's seat backs below
const NEAR = { back: 214, seat: 214 };
const L_DAD = look(26, 0, 1, '#c8202a', '#2a3044', { hat: '#1c2a52' });
const L_KID = look(15, 0, 3, '#e8e6de', '#2a3a6a', { shorts: 1, hat: '#c8202a', dk: 0.2 });
const L_HE = look(25, 2, 0, '#2a3a5e', '#2a2a30', { coat: 1 }), L_SHE = look(24, 1, 4, '#e8c8b0', '#2a2a3a', { long: 3 });
const L_SCORE = look(25, 1, 5, '#6a6a5a', '#3a3a30', { coat: 1, hat: '#2a2a30' });
const L_FILM = look(24, 3, 0, '#8a5a9a', '#1e2030', { long: 2 });
const L_FRA = look(25, 2, 2, '#e8e6de', '#3a4a6a', {}), L_FRB = look(25, 0, 1, '#2a2a30', '#2a2a36', { hat: '#c8202a' });
const L_TEEN = look(23, 1, 0, '#3a6aa8', '#2a2a36', {}), L_SWEAT = look(24, 0, 3, '#5a7a4a', '#2a2a30', { long: 3 });
const L_OLD = look(25, 0, 5, '#3a4a6a', '#3a3a40', { coat: 1, hat: '#c8202a' }), L_STRETCH = look(26, 3, 0, '#c8202a', '#2a2a36', {});
const L_VEND = look(30, 2, 0, '#e8b030', '#5a5a64', { hat: '#1e5a3a', dk: 0.2 });
// [x, look, rank, verb]: the cast of the near row
const CAST = [
  { x: 24, P: L_OLD, rank: 0.65, verb: 'old' }, { x: 46, P: L_HE, rank: 0.15, verb: 'he' }, { x: 56, P: L_SHE, rank: 0.15, verb: 'she' },
  { x: 92, P: L_SCORE, rank: 0.3, verb: 'score' }, { x: 176, P: L_DAD, rank: 0, verb: 'dad' }, { x: 189, P: L_KID, rank: 0, verb: 'kid' },
  { x: 250, P: L_STRETCH, rank: 0.92, verb: 'stretch' }, { x: 300, P: L_FILM, rank: 0.4, verb: 'film' },
  { x: 352, P: L_FRA, rank: 0.55, verb: 'fra' }, { x: 363, P: L_FRB, rank: 0.55, verb: 'frb' },
  { x: 410, P: L_TEEN, rank: 0.75, verb: 'teen' }, { x: 446, P: L_SWEAT, rank: 0.85, verb: 'sweat' },
];

/* ================= The game ================= */
// The eighth inning on keyframes. Each pitch is released at its time t; the ball takes 0.42 s to the plate. The top
// half starts with two out (New York batting), the bottom from none (Boston batting): every pitch, out and hit below
// is what the loop shows, so the count on the board follows it.
const PITCHES = [
  { t: 8.6, k: 'ball', b: 'A' }, { t: 16.6, k: 'called', b: 'A' }, { t: FOUL.t, k: 'popfoul', b: 'A' }, { t: 36.6, k: 'ball', b: 'A' },
  { t: 46.6, k: 'foul', b: 'A' }, { t: 52.6, k: 'ball', b: 'A' }, { t: SINGLE.t, k: 'single', b: 'A' },
  { t: 72.6, k: 'called', b: 'B' }, { t: 80.6, k: 'ball', b: 'B' }, { t: FLYOUT.t, k: 'flyout', b: 'B' },
  { t: 106.6, k: 'warm' }, { t: 110.6, k: 'warm' }, { t: 114.6, k: 'warm' },
  { t: 152.6, k: 'grounder', b: 'C', to: 'ss' }, { t: 158.6, k: 'ball', b: 'D' }, { t: 162.6, k: 'called', b: 'D' }, { t: CAROM.t, k: 'carom', b: 'D' },
  { t: 176.6, k: 'foul', b: 'E' }, { t: 181.6, k: 'ball', b: 'E' }, { t: HR.t, k: 'hr', b: 'E' },
  { t: 210.6, k: 'grounder', b: 'F', to: 'third' }, { t: 214.6, k: 'called', b: 'G' }, { t: 218.6, k: 'swing', b: 'G' }, { t: KO.t, k: 'ko', b: 'G' },
  { t: 236.6, k: 'warm' }, { t: 0.6, k: 'warm' }, { t: 4.6, k: 'warm' },
];
// the count after each pitch [balls, strikes, outs], worked out once
const COUNT = new Map();
{
  let b = 0, s = 0, o = 2, cur = 'A';
  for (const p of PITCHES) {
    if (p.k === 'warm') continue;
    if (p.b !== cur) { b = 0; s = 0; cur = p.b; }
    if (p.t > BOTTOM.t0 && o === 3) o = 0;
    if (p.k === 'ball') b++;
    else if (p.k === 'called' || p.k === 'swing') s++;
    else if ((p.k === 'foul' || p.k === 'popfoul') && s < 2) s++;
    else if (p.k === 'ko') { o++; b = 0; s = 0; }
    else if (p.k === 'flyout' || p.k === 'grounder') { o++; b = 0; s = 0; }
    else if (p.k === 'single' || p.k === 'carom' || p.k === 'hr') { b = 0; s = 0; }
    COUNT.set(p, [b, s, o]);
  }
}
const lastPitch = t => { let q = null; for (const p of PITCHES) if (p.k !== 'warm' && p.t + 0.5 <= t && (!q || p.t > q.t)) q = p; return q; };
/** the count on the board at t: before the first pitch of the loop, the end of the strikeout's (reset to 2 out) */
function countAt(t) {
  if (t < 8.6 + 0.5 || t >= CHANGE[1].t0) return [0, 0, 2];
  if (inWin(t, CHANGE[0].t0, BOTTOM.t0 + 4)) return [0, 0, 0];
  const p = lastPitch(t), c = COUNT.get(p);
  return c[2] === 3 ? [0, 0, 3] : c;
}
// who fields: Boston (home whites) in the top half, New York (road greys) in the bottom; each side's dugout
const TEAM = {
  bos: { shirt: '#ecebe2', pants: '#e0dfd6', cap: '#1c2a52', sock: '#b82030', dug: [70, -26] },
  ny: { shirt: '#9a9ca6', pants: '#8e909a', cap: '#1c2440', sock: '#1c2440', dug: [-26, 70] },
};
const UMP = { shirt: '#1e2230', pants: '#4a4c56', cap: '#14161e', sock: '#14161e' };
const POS = { p: RUBBER, c: [-2.4, -2.4], first: [96, 12], second: [124, 76], ss: [70, 130], third: [12, 104],
  lf: [72, 262], cf: [214, 222], rf: [262, 72] };
const ROLES = Object.keys(POS);
const fielding = t => inWin(t, CHANGE[0].t0 + 9, CHANGE[1].t0 + 9) ? 'ny' : 'bos';
/** how far through a side change (0 none, 0..1 going off / coming on) */
function changeAt(t) {
  for (const c of CHANGE) {
    const tau = ((t - c.t0) % LOOP + LOOP) % LOOP;
    if (tau < c.t1 - c.t0) return { c, tau };
  }
  return null;
}
/** a fielder's spot as he shifts a little between batters */
const driftPos = (role, t) => [POS[role][0] + 3 * wave(t, 6, ROLES.indexOf(role) * 0.13), POS[role][1] + 2 * wave(t, 4, ROLES.indexOf(role) * 0.29)];
/** a fielder's spot and pose at t; null while in the dugout */
function fielderAt(role, t) {
  const team = fielding(t), base = POS[role], ch = changeAt(t), stagger = ROLES.indexOf(role) * 0.5;
  let [x, y] = driftPos(role, t), pose = 'stand', dir = 1;
  if (role === 'p' || role === 'c') { x = base[0]; y = base[1]; }
  if (ch) {                                                                                     // off to the dugout, then the others on
    const dug = TEAM[ch.c.off === team ? ch.c.off : ch.c.on].dug, goingOff = ch.c.off === team;
    const u = goingOff ? clamp((ch.tau - stagger) / 8, 0, 1) : clamp((ch.tau - 9 - stagger) / 8, 0, 1);
    if (goingOff && u >= 1) return null;
    if (!goingOff && u <= 0) return null;
    const e = u * u * (3 - 2 * u), from = goingOff ? [x, y] : dug, to = goingOff ? dug : [x, y];
    return { x: lerp(from[0], to[0], e), y: lerp(from[1], to[1], e), pose: 'run', team, dir: to[0] > from[0] ? 1 : -1, step: Math.floor(t * 5) & 1 };
  }
  // reactions to the batted balls
  const play = (p, a, b) => inWin(t, p.t + a, p.t + b);
  if (role === 'lf' && play(SINGLE, 0.4, 8)) { const u = smooth(SINGLE.t + 0.4, SINGLE.t + 2.8, t); x = lerp(x, 46, u); y = lerp(y, 242, u); pose = t < SINGLE.t + 2.9 ? 'run' : t < SINGLE.t + 3.6 ? 'throw' : 'stand'; }
  if (role === 'cf' && play(FLYOUT, 0.4, 7)) { const u = smooth(FLYOUT.t + 0.4, FLYOUT.t + 4.2, t); x = lerp(x, 212, u); y = lerp(y, 296, u); pose = t < FLYOUT.t + 4.2 ? 'run' : t < FLYOUT.t + 5 ? 'catch' : 'stand'; }
  if (role === 'lf' && play(CAROM, 0.3, 9)) { const u = smooth(CAROM.t + 0.3, CAROM.t + 3.4, t); x = lerp(x, 50, u); y = lerp(y, 284, u); pose = t < CAROM.t + 3.5 ? 'run' : t < CAROM.t + 4.4 ? 'throw' : 'stand'; }
  if ((role === 'lf' || role === 'cf') && play(HR, 0.3, 12)) { const u = smooth(HR.t + 0.3, HR.t + 3.5, t); y = lerp(y, role === 'lf' ? 296 : 300, u); x = lerp(x, role === 'lf' ? 110 : 196, u); pose = t < HR.t + 3.5 ? 'run' : 'stand'; }
  if (role === 'first' && play(FOUL, 0.5, 6)) { const u = smooth(FOUL.t + 0.5, FOUL.t + 4, t); x = lerp(x, 70, u); y = lerp(y, -14, u); pose = t < FOUL.t + 4 ? 'run' : 'stand'; }
  for (const g of PITCHES) if (g.k === 'grounder' && role === g.to && play(g, 0.4, 4)) { const u = smooth(g.t + 0.4, g.t + 1.4, t); x = lerp(x, x - 8, u); pose = t < g.t + 1.5 ? 'run' : t < g.t + 2.3 ? 'throw' : 'stand'; }
  if (role === 'first') for (const g of PITCHES) if (g.k === 'grounder' && play(g, 1, 4)) { x = 90; y = 3; pose = t > g.t + 2.4 && t < g.t + 3 ? 'catch' : 'stand'; }
  const tz = tossAt(t);
  if (tz && tz.thrower === role) pose = tz.u < 0.15 ? 'throw' : 'stand';
  if (tz && tz.catcher === role && tz.u > 0.8) pose = 'catch';
  // the ready step on every pitch
  const lp = PITCHES.find(p => p.k !== 'warm' && inWin(t, p.t - 0.3, p.t + 1.1));
  if (lp && pose === 'stand' && role !== 'p' && role !== 'c') pose = 'ready';
  if (role === 'p') pose = pitcherPose(t);
  if (role === 'c') pose = 'crouch';
  return { x, y, pose, team, dir, step: Math.floor(t * 5) & 1 };
}
/** the pitcher: set, the leg kick, the delivery, the follow-through */
function pitcherPose(t) {
  for (const p of PITCHES) {
    const tau = t - p.t;
    if (tau > -1.6 && tau < -0.9) return 'set';
    if (tau >= -0.9 && tau < -0.25) return 'kick';
    if (tau >= -0.25 && tau < 0.3) return 'throw';
    if (tau >= 0.3 && tau < 0.9) return 'follow';
  }
  return 'stand';
}

// the batters of the loop: when each walks up, stands in, and what he does after (the bases: 0 home, 90 first ...)
const BATTERS = {
  A: { team: 'ny', inT: 2, at: 6, hand: 1, run: { t: SINGLE.t + 0.45, to: 90, hold: FLYOUT.t + 0.6, after: 'off' } },
  B: { team: 'ny', inT: 64, at: 68, hand: 1, out: FLYOUT.t + 0.6 },
  C: { team: 'bos', inT: 146, at: 150, hand: 1, run: { t: 153.05, to: 90, out: 157 } },
  D: { team: 'bos', inT: 153, at: 157.5, hand: -1, run: { t: CAROM.t + 0.45, to: 90, hold: HR.t + 0.45, home: true } },
  E: { team: 'bos', inT: 171, at: 175, hand: 1, run: { t: HR.t + 0.45, to: 360, trot: true } },
  F: { team: 'bos', inT: 205, at: 209, hand: 1, run: { t: 211.05, to: 90, out: 215 } },
  G: { team: 'bos', inT: 211, at: 214, hand: 1, out: KO.t + 0.6 },
};
const ONDECK = { bos: [36, -22], ny: [-22, 36] };
/** a point on the base paths, dist ft from home round the diamond */
function basePt(dist) {
  const d = clamp(dist, 0, 360);
  if (d < 90) return [d, 0]; if (d < 180) return [90, d - 90]; if (d < 270) return [270 - d, 90]; return [0, 360 - d];
}
/** everyone on the field at t besides the fielders: batters, runners, umpires, coaches: [{ x, y, pose, kit, dir, step }] */
function othersAt(t, list) {
  for (const [id, B] of Object.entries(BATTERS)) {
    const kit = TEAM[B.team], dug = TEAM[B.team].dug, box = [-2.2 * B.hand, 2.2 * B.hand], step = Math.floor(t * 5) & 1;
    if (t < B.inT) continue;
    if (t < B.at) { const u = (t - B.inT) / (B.at - B.inT), od = ONDECK[B.team]; list.push({ x: lerp(od[0], box[0], u), y: lerp(od[1], box[1], u), pose: 'walk', kit, step, bat: 1 }); continue; }
    const run = B.run;
    if (!run || t < run.t) {
      if (B.out && t >= B.out) { const u = clamp((t - B.out) / 5, 0, 1); if (u < 1) list.push({ x: lerp(box[0], dug[0], u), y: lerp(box[1], dug[1], u), pose: 'walk', kit, step, bat: 1 }); continue; }
      const sw = PITCHES.find(p => p.b === id && inWin(t, p.t + 0.3, p.t + 0.75) && /swing|foul|ko|single|carom|hr|grounder|flyout/.test(p.k));
      list.push({ x: box[0], y: box[1], pose: sw ? 'swing' : 'stance', kit, hand: B.hand, bat: 1 });
      continue;
    }
    // running: to first (and out, or holding), or round the bases
    const speed = run.trot ? 19 : 22;
    let dist = Math.min(run.to, (t - run.t) * speed), pose = 'run';
    if (run.home && t >= run.hold) dist = 90 + Math.min(270, (t - run.hold) * 20);
    if (run.out && t >= run.out) { const u = clamp((t - run.out) / 6, 0, 1); if (u < 1) list.push({ x: lerp(96, dug[0], u), y: lerp(2, dug[1], u), pose: 'walk', kit, step }); continue; }
    if (dist >= run.to && !run.home && !run.trot) { pose = 'stand'; dist = 90; }
    if (run.after === 'off' && t >= run.hold) { const u = clamp((t - run.hold) / 7, 0, 1); if (u < 1) list.push({ x: lerp(92, TEAM.ny.dug[0], u), y: lerp(6, TEAM.ny.dug[1], u), pose: 'run', kit, step }); continue; }
    if ((run.home || run.trot) && dist >= 360) { const u = clamp((t - (run.home ? run.hold + 13.5 : run.t + 360 / speed)) / 5, 0, 1); if (u < 1) list.push({ x: lerp(0, dug[0], u), y: lerp(0, dug[1], u), pose: 'walk', kit, step }); continue; }
    const [x, y] = basePt(dist), lead = pose === 'stand' ? 6 : 0;
    list.push({ x: x + lead * 0.3, y: y + lead, pose, kit, step, dir: 1 });
  }
  const bat = Object.values(BATTERS).find(B => inWin(t, B.inT - 6, (B.run?.hold ?? B.out ?? B.run?.out ?? 240) + 2))?.team ?? (fielding(t) === 'bos' ? 'ny' : 'bos');
  const pitch = PITCHES.some(p => p.k !== 'warm' && inWin(t, p.t - 1.6, p.t + 1.2));
  list.push({ x: -5.6, y: -5.6, pose: pitch ? 'umpcrouch' : 'stand', kit: UMP });
  list.push({ x: -10, y: 104, pose: 'stand', kit: UMP }); list.push({ x: 128, y: 116, pose: 'stand', kit: UMP }); list.push({ x: 102, y: -9, pose: 'stand', kit: UMP });
  const ch = changeAt(t), coachKit = TEAM[fielding(t) === 'bos' ? 'ny' : 'bos'];
  if (!ch) { list.push({ x: -16, y: 100, pose: 'stand', kit: coachKit }); list.push({ x: 100, y: -16, pose: 'stand', kit: coachKit }); }
  return list;
}
/** the ball at t: { x, y, z } in feet, or null */
function ballAt(t) {
  for (const p of PITCHES) {
    const tau = t - p.t;
    if (tau < -0.02 || tau > 6.2) continue;
    if (tau < 0.42) { const u = tau / 0.42; return { x: lerp(39, -1, u), y: lerp(39, -1, u), z: lerp(6, 2.6, u) - 1.2 * u * u }; }
    const arc = (a, b, t0, t1, h) => { const u = clamp((tau - t0) / (t1 - t0), 0, 1); return { x: lerp(a[0], b[0], u), y: lerp(a[1], b[1], u), z: lerp(a[2], b[2], u) + 4 * h * u * (1 - u) }; };
    const k = p.k;
    if (k === 'ball' || k === 'called' || k === 'swing' || k === 'ko' || k === 'warm') { if (tau > 1.5 && tau < 2.3 && k !== 'ko') return arc([-2, -2, 4], [40, 40, 6], 1.5, 2.3, 7); continue; }
    if (k === 'foul') { if (tau < 1.0) return arc([0, 0, 3], [-30, -90, 26], 0.42, 1.0, 10); continue; }
    if (k === 'popfoul') { if (tau < FOUL.land - FOUL.t) return arc([0, 0, 3], [64, -48, 12], 0.42, FOUL.land - FOUL.t, 112); continue; }
    if (k === 'single') {
      if (tau < 2.9) { const u = (tau - 0.42) / 2.48, b = Math.abs(Math.sin(u * Math.PI * 3)) * 6 * (1 - u); return { x: lerp(0, 46, u), y: lerp(0, 242, u), z: b }; }
      if (tau > 3.6 && tau < 5) return arc([46, 242, 6], [90, 90, 5], 3.6, 5, 18);
      continue;
    }
    if (k === 'flyout') { if (tau < 4.4) return arc([0, 0, 3], [212, 296, 7], 0.42, 4.4, 102); continue; }
    if (k === 'grounder') {
      const f = POS[p.to];
      if (tau < 1.4) { const u = (tau - 0.42) / 0.98; return { x: lerp(0, f[0] - 8, u), y: lerp(0, f[1], u), z: Math.abs(Math.sin(u * Math.PI * 2)) * 2 * (1 - u) }; }
      if (tau > 1.6 && tau < 2.4) return arc([f[0] - 8, f[1], 5], [90, 3, 5], 1.6, 2.4, 6);
      continue;
    }
    if (k === 'carom') {
      if (tau < 2.1) return arc([0, 0, 3], [55, 310, 14], 0.42, 2.1, 18);
      if (tau < 3.1) return arc([55, 310, 14], [50, 286, 0], 2.1, 3.1, 4);
      if (tau < 3.5) return { x: 50, y: 285, z: 0 };
      if (tau > 3.7 && tau < 5.2) return arc([50, 284, 6], [90, 90, 5], 3.7, 5.2, 22);
      continue;
    }
    if (k === 'hr') { if (tau < 5.4) return arc([0, 0, 3], [168, 372, 40], 0.42, 5.4, 78.5); continue; }
  }
  return null;
}
// between the halves the outfielders loosen up, throwing to each other (a toss every 3 s); New York's reliever warms up
// in the bullpen through the bottom half, throwing to his catcher every 4 s (behind the bullpen's low wall)
const TOSS = { t0: 115, t1: 146, p: 3 };
const PEN = { t0: 150, t1: 222, p: 4, mound: [372, 168], plate: [356, 200] };
function tossAt(t) {
  if (!inWin(t, TOSS.t0, TOSS.t1)) return null;
  const k = Math.floor((t - TOSS.t0) / TOSS.p), u = ((t - TOSS.t0) % TOSS.p) / 1.4, a = driftPos(k & 1 ? 'cf' : 'lf', t), b = driftPos(k & 1 ? 'lf' : 'cf', t);
  if (u > 1) return { thrower: null };
  return { x: lerp(a[0], b[0], u), y: lerp(a[1], b[1], u), z: 6 + 26 * 4 * u * (1 - u), thrower: k & 1 ? 'cf' : 'lf', catcher: k & 1 ? 'lf' : 'cf', u };
}
function drawPen(t, crowd) {
  if (crowd <= 0) return;
  const kit = TEAM[inWin(t, PEN.t0 - 10, PEN.t1 + 4) ? 'ny' : 'bos'], warm = inWin(t, PEN.t0, PEN.t1);
  const tau = ((t - PEN.t0) % PEN.p + PEN.p) % PEN.p, [mx, my] = PEN.mound, [cx, cy] = PEN.plate;
  const [sx, sy, d] = P(mx, my), [qx, qy, dq] = P(cx, cy);
  player(sx, sy, Math.round(CAM.f * 6.1 / d), { kit, pose: warm ? (tau < 0.6 ? 'kick' : tau < 1 ? 'throw' : 'stand') : 'stand', dir: -1 }, SKINS[1]);
  player(qx, qy, Math.round(CAM.f * 6.1 / dq), { kit, pose: 'crouch', dir: 1 }, SKINS[2]);
  if (warm && tau >= 0.9 && tau < 1.35) { const u = (tau - 0.9) / 0.45, [bx, by] = P(lerp(mx, cx, u), lerp(my, cy, u), 5 - u * 2); dot(bx, by, '#ffffff'); }
  if (warm && tau >= 2 && tau < 2.9) { const u = (tau - 2) / 0.9, [bx, by] = P(lerp(cx, mx, u), lerp(cy, my, u), 4 + 10 * u * (1 - u)); dot(bx, by, '#ffffff'); }
}
/** a ballplayer (or umpire) hpx tall at screen (x, y) */
function player(x, y, hpx, q, skin) {
  x = Math.round(x); y = Math.round(y);
  const k = q.kit, pose = q.pose, big = hpx >= 10, hs = hpx >= 14 ? 3 : big ? 2 : 1, bw = hpx >= 14 ? 4 : big ? 3 : 2;
  let h = hpx;
  if (pose === 'crouch' || pose === 'umpcrouch') h = Math.round(hpx * 0.62);
  else if (pose === 'ready' || pose === 'set' || pose === 'stance') h = hpx - 1;
  const tt = Math.max(1, Math.round(h * 0.34)), ll = Math.max(1, h - hs - tt), top = y - h + 1, ty = top + hs, ly = ty + tt;
  ctx.globalAlpha = 0.28; ctx.fillStyle = '#04120a';                                           // the towers' shadows, several ways
  ctx.fillRect(x - bw, y + 1, bw * 2 + 1, 1); ctx.fillRect(x - bw - 2, y, 2, 1); ctx.fillRect(x + bw + 1, y, 2, 1);
  ctx.globalAlpha = 1;
  const xl = x - (bw >> 1);
  // legs
  if (pose === 'run' || pose === 'walk') {
    const s = q.step ? 1 : -1;
    R(xl, ly, bw, Math.max(1, ll - 1), k.pants);
    R(x + s, ly + 1, 1, ll - 1, k.pants); R(x - s, ly + 1, 1, ll - 2, k.pants);
    if (big) { R(x + s, y, 1, 1, k.sock); R(x - s, y - 1, 1, 1, k.sock); }
  } else if (pose === 'kick') {
    R(xl, ly, bw, ll, k.pants); R(xl - 1, ly, 2, 1, k.pants);
  } else if (pose === 'crouch' || pose === 'umpcrouch' || pose === 'ready') {
    R(xl - 1, ly, bw + 2, ll, k.pants); if (big) { R(xl - 1, y, 1, 1, k.sock); R(xl + bw, y, 1, 1, k.sock); }
  } else {
    R(xl, ly, bw, ll, k.pants);
    if (big && bw > 2) R(xl + 1, ly + 1, 1, ll - 1, mixHex(k.pants, '#000000', 0.35));
    if (big) { R(xl, y, 1, 1, k.sock); R(xl + bw - 1, y, 1, 1, k.sock); }
  }
  // the body and arms
  R(xl, ty, bw, tt, k.shirt);
  if (big) R(xl, ty + tt - 1, bw, 1, mixHex(k.shirt, '#1c2a52', 0.5));
  const arm = mixHex(k.shirt, '#000000', 0.15);
  if (pose === 'throw' || pose === 'kick') { R(xl - 1, ty - 1, 1, 2, arm); dot(xl - 1, ty - 2, skin); }
  else if (pose === 'follow') { R(xl + bw, ty + 1, 2, 1, arm); }
  else if (pose === 'catch') { R(xl - 1, top - 1, 1, hs + 1, arm); R(xl + bw, top - 1, 1, hs + 1, arm); dot(xl + bw, top - 2, '#5a3a20'); }
  else if (pose === 'set') { dot(x, ty + 1, '#5a3a20'); }
  else if (pose === 'ready' || pose === 'crouch') { R(xl - 1, ty + 1, 1, tt - 1, arm); R(xl + bw, ty + 1, 1, tt - 1, arm); if (pose === 'crouch') dot(xl + bw, ty + 1, '#3a2a1a'); }
  else { R(xl - 1, ty, 1, tt, arm); R(xl + bw, ty, 1, tt, arm); }
  // the bat: up over the shoulder in the stance, round in front through the swing
  if (q.bat && pose === 'stance') { const s = q.hand > 0 ? -1 : 1; for (let i = 0; i < Math.max(3, hs + 2); i++) dot(x + s * (1 + (i >> 1)), ty - i, '#c8a070'); }
  else if (q.bat && pose === 'swing') { const s = q.hand > 0 ? 1 : -1; R(s > 0 ? x : x - 5, ty + 1, 6, 1, '#c8a070'); }
  else if (q.bat && pose === 'walk') dot(xl + bw, ty + tt, '#c8a070');
  // the head and cap (a helmet for the batter and the runners)
  R(x - (hs >> 1), top, hs, hs, skin);
  R(x - (hs >> 1), top, hs, Math.max(1, hs - 1), k.cap);
  if (big) dot(x - (hs >> 1) + (q.dir > 0 ? hs : -1), top + hs - 2, k.cap);
}

/* ================= Prerendered state ================= */
let HL, SKY_C, WISPS, SKYLINE_C, CIT_C, TOWER_C, FIELD_C, BFRONT_C, WALLS_C, LFROOF_C, BLEACHB_C, VBF_C, MONTOP_C, PITCH, AD_C, BOARD_C, NEAR_C, WALK_C, VIG_C;
const SEATS_C = {}, CROWD_C = {};
const CROWD_IDS = ['lfg', 'mon', 'bleach', 'box3', 'box1'];
function buildStands() {
  let lap = 0;
  lap += seatRows('box1', WALL1, { o0: 5, o1: 60, dO: 3, z0: 2, slope: 0.2, dim: 0.34, sOff: lap, seed: 11, dMin: 132 });
  lap += 250;                                                                               // the right-field grandstand, out of sight
  lap += seatRows('bleach', BLEACH_WALL, { o0: 4, o1: 120, dO: 4, z0: 17, slope: 0.33, face: true, dim: 0.3, sOff: lap, seed: 12 });
  lap += seatRows('mon', [MON_B, MON_A], { o0: 3, o1: 19, dO: 4, z0: 37, slope: 0.3, face: true, dim: 0.22, sOff: lap, seed: 13 });
  lap += seatRows('lfg', LF_WALL.slice(0, 3), { o0: 5, o1: 100, dO: 3, z0: 2, slope: 0.4, face: true, dim: 0.36, sOff: lap, roof: { o: LF_ROOF.o }, seed: 14 });
  lap += seatRows('box3', [[-26, 100], [-30, 0], [-42, -42]], { o0: 5, o1: 60, dO: 3, z0: 2, slope: 0.2, dim: 0.36, sOff: lap, seed: 15, dMin: 132 });
  LAP_LEN = lap;
  for (const id of CROWD_IDS) {
    SEATS_C[id] = renderSeats(id, id === 'mon' ? '#1e4a30' : '#173a28', '#3a3e40');
    CROWD_C[id] = LEVELS.map(level => [renderCrowd(id, level, 0, 0), renderCrowd(id, level, 1, 1)]);
  }
}
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.45) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [4, 6, 16], Math.round(q * 0.36 * 255));
  }
  return p.done();
}
function prerender() {
  for (const L of [WINS]) L.length = 0;
  HL = {
    tower: makeHalo(60, '#dfe8ff', 0.11, 1.6), towerCore: makeHalo(22, '#f4f8ff', 0.3, 1.4), citgo: makeHalo(20, '#ffe8e8', 0.16, 1.5),
    citR: makeHalo(8, '#ff4048', 0.24, 1.3), tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), red: makeHalo(3, '#ff3a30', 0.5, 1),
    flash: makeHalo(5, '#f4f6ff', 0.5, 1.3), phone: makeHalo(2, '#cfe0ff', 0.4, 1), board: makeHalo(36, '#a8c0e0', 0.07, 1.5),
    sign: makeHalo(24, '#ffe2b0', 0.12, 1.5), field: makeHalo(120, '#9ad08a', 0.035, 1.4),
  };
  SKY_C = renderSky();
  WISPS = [makeWisps(31, 34, 5, '#262e58', '#444a70', 120), makeWisps(53, 30, 5, '#2a3258', '#4c4e70', 112), makeWisps(79, 30, 4, '#2e3458', '#56506a', 104)];
  const r = rng(2026);
  SKYLINE_C = renderSkyline(r);
  CIT_C = renderCitgo();
  TOWER_C = TOWERS.map(renderTower);
  FIELD_C = renderField();
  WALLS_C = renderWalls(); BFRONT_C = renderBleachFront();
  LFROOF_C = renderLFRoof();
  BLEACHB_C = renderBleachBack();
  VBF_C = renderBoardFrame();
  MONTOP_C = renderMonTop();
  buildStands();
  const all = CROWD_IDS.flatMap(id => SEC[id].fans.filter(f => !f.empty));
  PHONES = all.filter((f, i) => hash(i, 5, 71) < 0.03).map((f, i) => ({ f, k: 9 + Math.floor(hash(i, 1, 72) * 20), ph: hash(i, 2, 72) }));
  const ft = [HR.t + 1.2, HR.t + 1.9, HR.t + 2.6, HR.t + 3.4, HR.t + 5.1, HR.t + 7.3, WAVE.t0 + 6, WAVE.t0 + 28, SING.peak + 4, SING.peak + 16, 14, 47, 83, 122, 158, 171, 216, 232];
  FLASHES = ft.map((t, i) => ({ t, f: all[Math.floor(hash(i, 9, 73) * all.length)] }));
  AD_C = renderAd(); BOARD_C = renderBoardStruct(); NEAR_C = renderNearSeats(); WALK_C = renderWalkway();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky ================= */
const JETS = [{ k: 3, ph: 0.12, y0: 22, y1: 30, dir: 1 }, { k: 2, ph: 0.62, y0: 8, y1: 4, dir: -1 }];
const APPROACH = [{ t0: 40, t1: 70 }, { t0: 160, t1: 190 }];   // a plane on the approach to Logan, its lamps on
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.4 + 0.6 * Math.max(0, wave(t, s.k, s.ph))); ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  WISPS.forEach((c, i) => { const off = scroll(t, [2, 3, 2][i], W), y = [0, 24, 50][i]; ctx.drawImage(c, -off, y); ctx.drawImage(c, W - off, y); });
  for (const j of JETS) {
    const u = frac(j.k * t / LOOP + j.ph);
    if (u > 0.4) continue;
    const v = u / 0.4, x = j.dir > 0 ? lerp(-10, W + 10, v) : lerp(W + 10, -10, v), y = lerp(j.y0, j.y1, v);
    dot(x, y, '#b8bccc'); if (frac(t * 0.9 + j.ph) < 0.1) dot(x, y + 1, '#ff4a40');
  }
  for (const a of APPROACH) {
    if (!inWin(t, a.t0, a.t1)) continue;
    const u = (t - a.t0) / (a.t1 - a.t0), x = Math.round(lerp(330, 470, u)), y = Math.round(lerp(40, 58, u));
    dot(x, y, '#f4f6ff'); halo(HL.tiny, x, y, 0.8); R(x - 3, y, 3, 1, '#7a8090');
    if (frac(t * 1.1) < 0.12) dot(x - 2, y - 1, '#ff4a40');
  }
}
/* ================= Per-frame: beyond the wall ================= */
const winLit = (w, t) => frac(w.k * t / LOOP + w.ph) < w.duty;
function drawFar(t, pitch, billboard, crowd) {
  halo(HL.field, 230, 120, 1);
  blit(SKYLINE_C);
  for (const w of WINS) if (winLit(w, t)) R(w.x, w.y, w.w, w.h, w.c);
  // the Citgo sign: its LEDs burn steady, its glow breathes a little in the haze
  halo(HL.citgo, CIT.cx, CIT.cy, 0.82 + 0.1 * wave(t, 12));
  halo(HL.citR, CIT.cx, CIT.y + 8, 0.7 + 0.1 * wave(t, 12, 0.3));
  ctx.drawImage(CIT_C, CIT.x, CIT.y);
  blit(BLEACHB_C);
  drawBoard(t, crowd);
  // the light towers, their lamps' glow, the moths and the bats round them
  TOWERS.forEach((T, i) => { blit(TOWER_C[i]); });
  blit(MONTOP_C);
  if (billboard) drawBillboard(t, pitch);
}
function drawGlare(t, reducedMotion) {
  TOWERS.forEach((T, i) => {
    halo(HL.tower, T.x, T.top + T.hh / 2, 0.93 + 0.03 * wave(t, 900, i * 0.37));                // the humid air shimmering in the beams
    halo(HL.towerCore, T.x, T.top + T.hh / 2, 0.9 + 0.04 * wave(t, 960, i * 0.3));
    for (let k = 0; k < 7; k++) {                                                              // moths in the lamps' light
      const a = TAU * ((22 + k * 5 + i * 3) * t / LOOP + k / 7), r = T.hw + 3 + (k % 3) * 3 + 2 * Math.sin(TAU * (9 * t / LOOP + k));
      const x = T.x + r * Math.cos(a), y = T.top + T.hh / 2 + r * 0.5 * Math.sin(a * 1.3) + (hash(k, i, step(t, 4800)) - 0.5);
      ctx.globalAlpha = 0.7; dot(x, y, '#f4eed8'); ctx.globalAlpha = 1;
    }
  });
  BATS.forEach((b, i) => {                                                                     // bats hawking the moths over the park
    const x = Math.round(b.cx + b.ax * Math.sin(TAU * (b.kx * t / LOOP + b.ph)) + 6 * Math.sin(TAU * (b.kx * 5 * t / LOOP + i))), y = Math.round(b.cy + b.ay * Math.sin(TAU * (b.ky * t / LOOP + b.ph * 2)));
    ctx.fillStyle = '#0a0c1a';
    if ((step(t, 2880) + i) & 1) { ctx.fillRect(x - 1, y - 1, 1, 1); ctx.fillRect(x + 1, y - 1, 1, 1); ctx.fillRect(x, y, 1, 1); } else ctx.fillRect(x - 1, y, 3, 1);
  });
}
const BATS = [{ cx: 160, cy: 40, ax: 60, ay: 14, kx: 7, ky: 17, ph: 0.1 }, { cx: 300, cy: 46, ax: 70, ay: 12, kx: 9, ky: 19, ph: 0.45 }];

/* ================= Per-frame: the video board over the bleachers ================= */
// Calm: the score and the batter's card most of the loop; the crowd on the board in the wave; a warm glow in the
// singalong; HOME RUN after the home run, then the new score.
function txt(s, x, y, col) { ctx.fillStyle = col; stamp((px, py) => ctx.fillRect(px, py, 1, 1), s, x, y, col); }
function drawBoard(t, crowd) {
  const { x, y, w, h } = VB;
  blit(VBF_C);
  R(x, y, w, h, '#0a1426');
  if (crowd <= 0) t = 0;                            // an empty park: the board holds the score, nothing plays
  const bosR = inWin(t, HR.t + 1, 233) ? 5 : 3, mode = inWin(t, HR.t + 0.6, HR.t + 12) ? 'hr' : inWin(t, SING.t0 + 2, SING.t1 - 2) ? 'sing' : inWin(t, WAVE.t0 + 2, WAVE.t1 - 2) ? 'crowd' : 'score';
  if (mode === 'sing') {
    const e = env(t, SING.t0 + 2, SING.t1 - 2, 6), cx = x + (w >> 1), cy = y + (h >> 1);
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i += 2) { ctx.globalAlpha = (0.25 + 0.25 * e) * (1 - Math.abs(j - h / 2) / h); dot(x + i + (j & 1), y + j, '#c84a5a'); }
    ctx.globalAlpha = 0.55 + 0.25 * e * (0.5 + 0.5 * wave(t, 100));
    for (const [dx, dy, ww] of [[-4, -3, 3], [2, -3, 3], [-5, -2, 11], [-5, -1, 11], [-4, 0, 9], [-3, 1, 7], [-2, 2, 5], [-1, 3, 3], [0, 4, 1]]) R(cx + dx, cy + dy, ww, 1, '#ff8a9a');
    ctx.globalAlpha = 1;
  } else if (mode === 'crowd') {
    for (let j = 1; j < h - 1; j += 2) for (let i = 1; i < w - 1; i++) { const q = hash(i + Math.round(wave(t, 120, j * 0.1)), j, 61); if (q < 0.7) dot(x + i, y + j, ['#a82a30', '#d8d6ce', '#2a3460', '#c8a080'][Math.floor(q * 5.7) % 4]); }
  } else if (mode === 'hr') {
    const pulse = 0.7 + 0.3 * (0.5 + 0.5 * wave(t, 120));
    ctx.globalAlpha = pulse; txt('HOME RUN', x + Math.round((w - textW('HOME RUN')) / 2), y + 5, '#ffd860'); ctx.globalAlpha = 1;
    const score = `BOS ${bosR}  NY 3`;
    txt(score, x + Math.round((w - textW(score)) / 2), y + 14, '#e8ecf4');
  } else {
    txt('NY', x + 3, y + 4, '#c8ccd8'); txt(String(3), x + 18, y + 4, '#ffffff');
    txt('BOS', x + 3, y + 12, '#c8ccd8'); txt(String(bosR), x + 18, y + 12, '#ffffff');
    R(x + 24, y + 3, 1, h - 6, '#2a3a5a');
    const bat = crowd > 0 && Object.entries(BATTERS).find(([, B]) => inWin(t, B.inT, (B.run?.t ?? B.out ?? 240) + 2));
    const num = bat ? String(10 + Math.floor(hash(bat[0].charCodeAt(0), 3) * 40)) : '';
    R(x + 28, y + 3, 11, h - 6, '#1a2a48'); R(x + 31, y + 6, 5, 4, '#c8b49a'); R(x + 30, y + 10, 7, h - 13, bat && BATTERS[bat[0]].team === 'bos' ? '#e8e6de' : '#9a9ca6');
    if (num) txt(num, x + 43, y + 4, '#ffffff');
    txt('8TH', x + 43, y + 12, '#8a9ab8');
    const [b, s, o] = crowd > 0 ? countAt(t) : [0, 0, 0];
    for (let i = 0; i < 3; i++) dot(x + 62 + i * 2, y + 5, i < b ? '#7aff8a' : '#203028');
    for (let i = 0; i < 2; i++) dot(x + 62 + i * 2, y + 9, i < s ? '#ffcc40' : '#302a18');
    for (let i = 0; i < 2; i++) dot(x + 62 + i * 2, y + 13, i < o ? '#ff5040' : '#301a18');
  }
  halo(HL.board, x + (w >> 1), y + (h >> 1), 0.8);
}

/* ================= Per-frame: the Monster's scoreboard ================= */
const OPS_DUR = 5.5;
function plateAt(slot, t) {                        // what a changing plate shows at t
  if (slot === 'ny8') return inWin(t, 99, 232.6) ? '0' : '';
  if (slot === 'bos8') return inWin(t, 207, 233.1) ? '2' : '';
  if (slot === 'bosR') return inWin(t, 207.5, 233.6) ? '5' : '3';
  return '';
}
const OP_SLOT = { 96.5: ['ny8'], 204.5: ['bos8', 'bosR'], 230.5: ['ny8', 'bos8', 'bosR'] };
const SLOT_XY = { ny8: ['ny', 7], bos8: ['bos', 7], bosR: ['bos', 'runs'] };
function drawScoreboard(t, crowd) {
  if (crowd <= 0) t = 0;                            // no game: the plates as they stand, the lamps dark
  const put = (s, team, col, ink) => { const [x, y] = slotXY(team, col); R(x, y, 3, 5, PLATE_BG); txt(s, x, y, ink); };
  for (const team of ['ny', 'bos']) {
    for (let i = 0; i < 9; i++) {
      let v = LINE[team][i];
      if (i === 7) v = plateAt(team === 'ny' ? 'ny8' : 'bos8', t);
      if (v) put(v, team, i, PLATE);
    }
    put(team === 'ny' ? '3' : plateAt('bosR', t), team, 'runs', '#ffe8a0');
  }
  const [b, s, o] = crowd > 0 ? countAt(t) : [0, 0, 0];                                        // the ball-strike-out lamps
  const lamp = (i, y, on, col) => { const x = LS.lamps + i * 2; dot(x, y, on ? col : '#1a2a1e'); if (on) { ctx.globalAlpha = 0.35; dot(x, y - 1, col); ctx.globalAlpha = 1; } };
  for (let i = 0; i < 3; i++) lamp(i, PANEL.t + 3, i < b, '#9aff70');
  for (let i = 0; i < 2; i++) lamp(i, PANEL.t + 7, i < s, '#ffc040');
  for (let i = 0; i < 2; i++) lamp(i, PANEL.t + 11, i < o, '#ff5040');
  // the operator inside the wall: a slot goes dark, his hand takes the plate and slides the new one in
  for (const op of OPS) {
    const tau = crowd > 0 ? t - op.t : -1;
    if (tau < 0 || tau > OPS_DUR) continue;
    OP_SLOT[op.t].forEach((slot, i) => {
      const [x, y] = slotXY(...SLOT_XY[slot]), a = 0.6 + i * 0.5;
      if (tau > a && tau < a + 3) { R(x - 1, y - 1, 5, 7, '#020604'); if (tau > a + 0.6 && tau < a + 2.6) R(x, y + 1 + Math.round(2 * Math.sin(Math.PI * (tau - a - 0.6) / 2)), 2, 2, '#c8906a'); }
    });
  }
}

/* ================= Per-frame: the field, the players, the ball ================= */
function drawGame(t, crowd) {
  if (crowd <= 0) return;
  const list = [];
  for (const role of ROLES) { const f = fielderAt(role, t); if (f) list.push({ x: f.x, y: f.y, pose: f.pose, kit: TEAM[f.team], dir: f.dir, step: f.step }); }
  othersAt(t, list);
  const drawn = list.map(q => { const [sx, sy, d] = P(q.x, q.y, 0); return { q, sx, sy, d }; }).filter(o => o.d > 30 && o.sx > -10 && o.sx < W + 10).sort((a, b) => b.d - a.d);
  for (const { q, sx, sy, d } of drawn) player(sx, sy, Math.max(5, Math.round(CAM.f * 6.1 / d)), q, SKINS[Math.floor(hash(Math.round(q.x), Math.round(q.y)) * 3)]);
  const tz = tossAt(t);
  if (tz && tz.thrower) { const [sx, sy] = P(tz.x, tz.y, tz.z); dot(sx, sy, '#ffffff'); }
  const b = ballAt(t);
  if (b) {
    const [sx, sy, d] = P(b.x, b.y, b.z);
    const beyond = b.y > 318 && sy > MON.top - 5;
    if (!beyond && d > 20) {
      if (b.z > 1) { const [gx, gy] = P(b.x, b.y, 0); if (gy > HY + 4 && gy < H) { ctx.globalAlpha = 0.3; dot(gx, gy, '#04120a'); ctx.globalAlpha = 1; } }
      const fly = b.z > 20;                                                                    // a ball in the air leaves a short trail
      for (let k = fly ? 6 : 1; k >= 1; k--) {
        const pb = ballAt(t - 0.05 * k);
        if (!pb) continue;
        const [px2, py2] = P(pb.x, pb.y, pb.z);
        ctx.globalAlpha = 0.4 * (1 - k / 7); dot(px2, py2, '#ffffff');
      }
      ctx.globalAlpha = 1;
      dot(sx, sy, '#ffffff');
      if (fly && d < 380) { dot(sx + 1, sy, '#e8ecf0'); dot(sx, sy + 1, '#d8dce4'); }
    }
  }
}

/* ================= Per-frame: the crowd ================= */
const RISE = { box1: 0.2, box3: 0.4, lfg: 0.9, bleach: 1.3, mon: 1.8 };   // the home run: how long after the crack each stand is up
let PHONES = [], FLASHES = [];
const levelOf = crowd => { const d = Math.min(1, crowd / CROWD); return d > 0.66 ? 0 : d > 0.33 ? 1 : 2; };
/** how far a stand is on its feet with its arms up (0..1): the home run, the singalong */
function upAt(id, t) {
  const r = HR.t + 0.45 + RISE[id], hr = smooth(r, r + 0.8, t) * (1 - smooth(HR.t + 15 + RISE[id] * 2, HR.t + 21 + RISE[id] * 2, t));
  const sing = env(t, SING.t0 + 3 + RISE[id] * 2, SING.fall + 4, 5);
  return Math.max(hr, sing);
}
/** the wave's front (ft round the park) and its width, or null */
function waveAt(t) {
  if (!inWin(t, WAVE.t0, WAVE.t1)) return null;
  return { wf: (t - WAVE.t0) * LAP_LEN / WAVE.lap, band: (24 + 26 * smooth(WAVE.t0, WAVE.t0 + 20, t)) * env(t, WAVE.t0, WAVE.t1, 3) };
}
const putCtx = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); };
function drawStand(id, t, crowd) {
  blit(SEATS_C[id]);
  if (crowd <= 0) return;
  const li = levelOf(crowd), lv = LEVELS[li], [seat, stand] = CROWD_C[id][li], up = upAt(id, t);
  if (up < 0.98) blit(seat);
  if (up > 0.02) {
    const sing = inWin(t, SING.t0, SING.t1 + 2);
    ctx.globalAlpha = Math.min(1, up * 1.4);
    if (sing) for (let y = 0; y < stand.h; y += 3) ctx.drawImage(stand.c, 0, y, stand.w, 3, stand.x + Math.round(wave(t, 100, (stand.y + y) * 0.013 + stand.x * 0.004)), stand.y + y, stand.w, 3);
    else blit(stand);
    ctx.globalAlpha = 1;
  }
  const wv = waveAt(t), S = SEC[id];
  if (wv && wv.band > 1) for (const f of S.fans) {                                           // the wave: the fans in its band stand, arms up
    if (f.empty || f.rank >= lv) continue;
    const dl = wv.wf - f.lap;
    if (dl < 0 || dl > LAP_LEN * 2) continue;
    if ((dl % LAP_LEN) < wv.band) fanDraw(putCtx, f, 1, (dl % LAP_LEN) < wv.band * 0.7 ? 1 : 0);
  }
  const n = S.fans.length, st = step(t, 14400);                                                // the crowd shifting in its seats
  for (let i = 0; i < 26; i++) {
    const f = S.fans[Math.floor(hash(i, st, id.length * 7) * n)];
    if (!f || f.empty || f.rank >= lv || f.sc < 1) continue;
    const hy = Math.round(f.sy - 4.2 * f.sc);
    dot(f.sx + (hash(i, st, 3) < 0.5 ? -1 : 1), hy + Math.round(0.8 * f.sc), f.shirt);
  }
}
function drawPhones(t, crowd, reducedMotion) {
  if (crowd <= 0) return;
  const lv = LEVELS[levelOf(crowd)], e = env(t, SING.t0 + 6, SING.fall + 4, 8);
  if (e > 0.01) for (const p of PHONES) {
    if (p.f.rank >= lv) continue;
    ctx.globalAlpha = e * (0.45 + 0.4 * (0.5 + 0.5 * wave(t, p.k, p.ph)));
    dot(p.f.sx + Math.round(wave(t, 100, p.f.sy * 0.013 + p.f.sx * 0.004)), p.f.sy - Math.round(6.4 * p.f.sc) - 1, '#e8f0ff');
  }
  ctx.globalAlpha = 1;
  for (const fl of FLASHES) {                                                                  // a camera flash now and then, soft
    if (!inWin(t, fl.t, fl.t + 0.14) || fl.f.rank >= lv) continue;
    const x = fl.f.sx, y = fl.f.sy - Math.round(5 * fl.f.sc);
    dot(x, y, '#ffffff');
    if (!reducedMotion) halo(HL.flash, x, y, 0.45);
  }
}

/* ================= Per-frame: the near rows ================= */
// The row in front of us, seen from behind over its seat backs, and the stairs of the section: a few fans up close,
// each doing something, and a vendor climbing the stairs, calling, selling to the father, and on up past us.
const VEND_ITEMS = [{ c: '#c8ccd4', d: '#7a7e88' }, { c: '#a8784a', d: '#6a4a2a' }, { c: '#e8e070', d: '#a8a040' }, { c: '#c8ccd4', d: '#7a7e88' }];
const stood = (verb, t) => {                       // when the people of the near row are on their feet
  const wv = waveAt(t);
  if (wv) { const dl = wv.wf - 12; if (dl >= 0 && (dl % LAP_LEN) < wv.band + 6) return 1; }
  if (inWin(t, HR.t + 0.6 + (verb === 'old' ? 1.5 : 0), HR.t + 17 + (verb.length % 3))) return 1;
  if (inWin(t, SING.peak - 2 + (verb.length % 4), SING.fall + 3)) return verb !== 'old' && verb !== 'score' ? 1 : 0;
  if (verb === 'kid' && inWin(t, FOUL.t + 1, FOUL.land + 1.5)) return 1;
  if (verb === 'stretch' && inWin(t, 128, 150)) return 1;
  return 0;
};
function drawHero(c, t) {
  const { x, P, verb } = c, up = stood(verb, t), hr = inWin(t, HR.t + 0.6, HR.t + 17), sing = inWin(t, SING.peak - 2, SING.fall + 3);
  const sway = sing ? Math.round(wave(t, 100, x * 0.004)) : 0, fy = up ? NEAR.seat + 7 : NEAR.seat;
  const o = { view: 0, sit: !up };
  if (up) { o.aL = 1; o.aR = 1; }
  if (verb === 'dad') {
    const point = !up && frac((t + 6) / 20) < 0.2, sale = vendSale(t);
    if (point) { o.aR = 4; o.lean = 1; }
    if (sale) o.aL = 3;
    if (!up && !sale && inWin(frac((t - 20) / 60) * 60, 21, 34)) o.aR = 2;                   // eating the hot dog he bought
    if (hr && frac(t / 2) < 0.5) o.aR = 5;
  } else if (verb === 'kid') {
    const lookUp = frac((t + 6) / 20) < 0.2;
    if (!up) { o.view = lookUp ? 2 : 0; o.dir = -1; }
    if (up && inWin(t, FOUL.t, FOUL.land + 2)) { o.aR = 0; o.aL = 1; }
  } else if (verb === 'he' || verb === 'she') {
    const eat = frac(t / 8 + (verb === 'she' ? 0.5 : 0)) < 0.2;
    if (!up && eat) o[verb === 'he' ? 'aR' : 'aL'] = 2;
    if (!up && frac(t / 30) < 0.14) o.lean = verb === 'he' ? 1 : -1;
    if (sing) { o.aL = verb === 'he' ? 1 : 5; o.aR = verb === 'he' ? 5 : 1; }
  } else if (verb === 'score') {
    const p = lastPitch(t), mark = p && inWin(t, p.t + 1.2, p.t + 2.8);
    if (!up) o.aR = mark && (step(t, 960) & 1) ? 2 : 0;
    if (hr) { o.aL = 3; o.aR = frac(t * 1.5) < 0.5 ? 3 : 0; }
  } else if (verb === 'film') {
    const film = inWin(t, WAVE.t0, WAVE.t1) || inWin(t, HR.t, HR.t + 14) || sing || inWin(t, FOUL.t, FOUL.land);
    if (film) { o.aL = 2; o.aR = 2; }
  } else if (verb === 'fra' || verb === 'frb') {
    const clink = frac((t + 9) / 40) < 0.05;
    if (!up) o[verb === 'fra' ? 'aR' : 'aL'] = clink ? 3 : frac(t / 11 + (verb === 'frb' ? 0.4 : 0)) < 0.15 ? 2 : 0;
    if (!up && verb === 'fra') o.lean = clink ? 1 : 0;
  } else if (verb === 'teen') {
    if (!up) o.aR = frac(t / 16) < 0.1 ? 1 : 0;
  } else if (verb === 'old') {
    if (!up) { o.view = frac(t / 24) < 0.15 ? 2 : 0; o.dir = 1; }
    if (hr) { o.aL = 3; o.aR = frac(t * 1.5) < 0.5 ? 3 : 0; }
  }
  const hy = person(x + sway, fy, P, o), ty = tyOf(P, fy, !up);
  // what they hold
  if (verb === 'kid') { const gx = o.aL === 1 ? x + sway - 3 : x + sway - 3, gy = o.aL === 1 ? hy - 3 : ty + 3; R(gx, gy, 2, 2, '#6a4222'); }
  if (verb === 'score' && !up) { R(x - 2, ty + 4, 4, 2, '#e8e6da'); if (o.aR === 2) dot(x + 2, ty + 3, '#e8c060'); }
  if ((verb === 'he' || verb === 'she') && !up && verb === 'she') R(x - 7, ty + 5, 4, 1, '#e8dcc0');
  if (verb === 'film' && o.aL === 2) { R(x - 1, hy - 3, 3, 2, '#9ad0ff'); halo(HL.phone, x, hy - 2, 0.7); }
  if ((verb === 'fra' || verb === 'frb') && !up) { const cx = verb === 'fra' ? x + 4 : x - 4; R(cx, ty + (o.aR === 2 || o.aL === 2 ? -1 : 3), 2, 3, '#e8c040'); }
  if (verb === 'teen' && (o.aR === 1)) R(x + 4, hy - 7, 2, 5, '#d8202a');
}
/** the vendor's sale to the father: true while the hot dog changes hands */
const vendSale = t => { const u = ((t % VENDOR_P) + VENDOR_P) % VENDOR_P; return u >= 15 && u < 19; };
// the walkway right below our seats, between us and the near row: the vendor comes along it every minute from the
// right, stops behind the father, calls out and sells him a hot dog, and walks on out to the left
function drawVendor(t) {
  const u = ((t % VENDOR_P) + VENDOR_P) % VENDOR_P, item = VEND_ITEMS[Math.floor(t / VENDOR_P) % 4];
  if (u >= 34) return;
  const x = Math.round(along([[0, W + 16], [12, 196], [22, 196], [34, -20]], u)), fy = WALK.feet, walking = u < 12 || u >= 22;
  const o = { view: walking ? 2 : 0, dir: u < 12 ? -1 : -1, step: walking ? Math.floor(x / 4) & 1 : 0 };
  if (!walking) { o.view = 2; o.dir = -1; o.aR = u >= 15 && u < 19 ? 3 : frac(u / 3) < 0.5 ? 2 : 0; }
  const hy = person(x, fy, L_VEND, o), ty = tyOf(L_VEND, fy);
  R(x - 4, ty + 3, 8, 5, item.c); R(x - 4, ty + 7, 8, 1, item.d); dot(x - 4, ty + 2, item.d);   // the tray on its strap
  if (u >= 15 && u < 19) { const k = (u - 15) / 4, hx = Math.round(lerp(x - 5, 180, k)), hy2 = Math.round(lerp(ty + 2, 207, k)); R(hx, hy2, 3, 1, '#d8a860'); dot(hx + 1, hy2, '#b83a2a'); }
}
// people walking the walkway: to the concourse and back, with food and drinks, a father carrying his son, an usher;
// their trousers are jeans, khakis and greys a step lighter than the concrete, so their legs show down to the shadow
const WALK = { feet: 251, top: 223, rail: 256 };
const L_SON = look(14, 2, 0, '#e8e6de', '#2a3a6a', { shorts: 1, hat: '#c8202a', dk: 0.26 });
const WALKERS = [
  { t0: 4, dur: 22, dir: 1, P: look(30, 1, 1, '#c8202a', '#3e5078', { dk: 0.3 }), mate: look(28, 0, 4, '#e8e6de', '#52628a', { long: 3, dk: 0.3 }), rank: 0.1, carry: 'beer' },
  { t0: 30, dur: 24, dir: -1, P: look(31, 2, 0, '#2a3a5e', '#6a6254', { hat: '#c8202a', dk: 0.3 }), rank: 0.2, carry: 'kid' },
  { t0: 50, dur: 20, dir: 1, P: look(29, 0, 3, '#d8b030', '#4a5a80', { long: 2, dk: 0.3 }), rank: 0.3, carry: 'lemon' },
  { t0: 74, dur: 22, dir: -1, P: look(30, 3, 0, '#c8202a', '#6e624a', { dk: 0.3 }), mate: look(30, 1, 2, '#2a2a30', '#48587a', { hat: '#1c2a52', dk: 0.3 }), rank: 0.45, carry: 'tray' },
  { t0: 98, dur: 26, dir: 1, P: look(30, 0, 5, '#3a4a6a', '#5e5e68', { coat: 1, hat: '#2a2a30', dk: 0.3 }), rank: 0.6, carry: '' },
  { t0: 124, dur: 22, dir: -1, P: look(31, 1, 0, '#b82a30', '#4c4c5a', { coat: 1, hat: '#1c2a52', dk: 0.28 }), rank: 0.05, carry: 'usher' },
  { t0: 150, dur: 21, dir: 1, P: look(28, 2, 4, '#8a5a9a', '#4a5a80', { long: 3, dk: 0.3 }), mate: look(18, 2, 0, '#e8e6de', '#2a3a6a', { shorts: 1, hat: '#c8202a', dk: 0.26 }), rank: 0.25, carry: 'candy' },
  { t0: 176, dur: 22, dir: -1, P: look(30, 0, 1, '#e8e6de', '#4e6088', { dk: 0.3 }), rank: 0.7, carry: 'dog' },
  { t0: 200, dur: 20, dir: 1, P: look(29, 3, 0, '#2e5a3a', '#6a604c', { hat: '#c8202a', dk: 0.3 }), rank: 0.85, carry: 'beer' },
  { t0: 216, dur: 22, dir: -1, P: look(30, 1, 3, '#c86a8a', '#4a5a80', { long: 3, dk: 0.3 }), rank: 0.4, carry: 'lemon' },
];
function drawWalkers(t, crowd) {
  for (const w of WALKERS) {
    if (!here(w.rank, crowd) || !inWin(t, w.t0, w.t0 + w.dur)) continue;
    const u = (t - w.t0) / w.dur, x = Math.round(w.dir > 0 ? lerp(-24, W + 24, u) : lerp(W + 24, -24, u)), f = Math.floor(x / 5) & 1;
    if (w.mate) person(x - w.dir * 12, WALK.feet - 1, w.mate, { view: 2, dir: w.dir, step: !f, aR: w.carry === 'candy' ? 2 : 0 });
    const o = { view: 2, dir: w.dir, step: f, aR: w.carry && w.carry !== 'kid' ? 3 : 0 };
    if (w.carry === 'kid') {                                                                 // his son on his shoulders, cap on, looking at the field:
      o.aR = 1;                                                                              // drawn first, so his father's head stays whole in front
      const fhy = tyOf(w.P, WALK.feet) - w.P.hh;
      person(x - w.dir, fhy + 1, L_SON, { view: 2, dir: w.dir, sit: true, aL: 0, aR: frac(t / 3) < 0.4 ? 4 : 0 });
    }
    const hy = person(x, WALK.feet, w.P, o), ty = tyOf(w.P, WALK.feet);
    if (w.carry === 'kid') {                                                                 // the boy's shin hanging down on his father's chest
      const lx = x + w.dir * 3;
      dot(lx, ty - 1, L_SON.bot); R(lx, ty, 1, 2, L_SON.skin); dot(lx, ty + 2, L_SON.shoe);
    }
    const hx = x + w.dir * 4;
    if (w.carry === 'beer') { R(hx, ty + 1, 2, 3, '#e8c040'); dot(hx, ty, '#f4f0e0'); }
    if (w.carry === 'lemon') { R(hx, ty, 2, 4, '#f0e070'); dot(hx + 1, ty - 1, '#e84a4a'); }
    if (w.carry === 'tray') { R(hx - 1, ty + 2, 5, 1, '#c8c4bc'); R(hx - 1, ty, 2, 2, '#e8c040'); R(hx + 2, ty, 2, 2, '#e8c040'); }
    if (w.carry === 'dog') { R(hx, ty + 2, 3, 1, '#d8a860'); dot(hx + 1, ty + 2, '#b83a2a'); }
    if (w.carry === 'candy') { const cx = x - w.dir * 12 + w.dir * 4, cy = tyOf(w.mate, WALK.feet - 1); R(cx - 1, cy - 5, 4, 3, '#f0a8c8'); dot(cx, cy - 2, '#e8e0d0'); }
    if (w.carry === 'usher') { R(x - 1, ty + 2, 2, 1, '#e8d070'); }
  }
}
function renderWalkway() {
  const p = pix(W, H);
  for (let y = NEAR.back - 11; y < NEAR.back + 2; y++) for (let x = 0; x < W; x++) {
    const k = y - NEAR.back + 11;
    p.set(x, y, k === 0 ? '#5a6a62' : k === 1 ? '#1a2620' : pick(['#22262a', '#2a2e32', '#32363a'], 0.45 + (vnoise(x / 5, y, 81) - 0.5) * 0.3, x, y));
  }
  for (let x = 0; x < W; x += 9) for (let y = NEAR.back - 14; y < NEAR.back - 10; y++) p.set(x, y, '#3a4a42');   // the rail's posts
  for (let x = 0; x < W; x++) p.set(x, NEAR.back - 14, '#4a5a52');
  return trim(p);
}
function renderNearSeats() {
  const p = pix(W, H);
  for (let x = 0; x < W; x++) {                                                               // the near row's seat backs
    const seatI = Math.floor((x + 3) / 13), edge = (x + 3) % 13 === 0;
    for (let y = NEAR.back; y < NEAR.back + 9; y++) {
      const k = y - NEAR.back;
      let col = k < 2 ? '#7a2a2c' : k < 6 ? (k % 3 === 2 ? '#3a1216' : '#5a1c20') : '#1a0a0c';
      if (edge) col = '#120708';
      if (k === 0 && hash(seatI, 3) < 0.3) col = '#94403a';
      p.set(x, y, col);
    }
  }
  for (let y = NEAR.back + 9; y < WALK.rail; y++) for (let x = 0; x < W; x++) {               // the walkway's concrete, lit from the field
    const k = y - NEAR.back - 9, v = 0.55 - k * 0.006 + (vnoise(x / 7, y / 2, 83) - 0.5) * 0.2 + (y === NEAR.back + 9 ? -0.3 : 0);
    p.set(x, y, pick(['#16191c', '#1e2226', '#262a2e', '#2e3236', '#363a3e'], v, x, y, 0.7));
    if (y > WALK.feet + 2 && (x * 7 + y) % 61 === 0) p.set(x, y, '#3e4246');
  }
  for (let x = 0; x < W; x++) {                                                               // the rail in front of our seats
    p.set(x, WALK.rail, '#5a7a64'); p.set(x, WALK.rail + 1, '#2a4232');
    for (let y = WALK.rail + 2; y < H; y++) p.set(x, y, x % 24 === 0 && y < WALK.rail + 9 ? '#2a4232' : pick(['#040607', '#07090a', '#0a0d0e'], 0.4 + (y - WALK.rail) * 0.02, x, y));
  }
  for (const [x, y] of [[300, WALK.rail - 3], [92, WALK.rail - 2]]) { p.fill(x, y, 3, 3, '#c8a040'); p.set(x, y - 1, '#e4e0d0'); }   // a cup and a program on the ledge
  return trim(p);
}

/* ================= The billboard: Dottie's, a Lansdowne Street hot-dog stand ================= */
// A painted board on two posts at the back of the Monster seats, lit by two gooseneck lamps. Its frame, posts, lamps and
// their glow are the billboard; without it the hotel's windows behind show.
const HOTDOG = ['.bbbbbbb.', 'rrryrrryr', '.bbbbbbb.'];
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#c8b890', '#e0d0a8', '#efe2bc'], 0.85 - j * 0.02, i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#8a2a20'); p.set(i, h - 1, '#8a2a20'); }
  const ink = (str, x, y, col) => stamp((px, py, c) => p.set(px, py, c), str, x, y, col);
  ink("DOTTIE'S", Math.round((w - textW("DOTTIE'S")) / 2), 3, '#b82a1e');       // two lines, centred, clear of the trim
  const key = { b: '#c8904a', r: '#a83a2a', y: '#e8c030' }, x2 = Math.round((w - (HOTDOG[0].length + 2 + textW('HOT DOGS'))) / 2);
  HOTDOG.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(x2 + i, 12 + j, key[row[i]]); });
  ink('HOT DOGS', x2 + HOTDOG[0].length + 2, 11, '#1e2e5a');
  return gradeAd(p.done(), { level: 0.6, tint: '#14182e', mix: 0.16, wear: 0.25, seed: 7 });
}
function renderBoardStruct() {
  const p = pix(W, H), L = BB.x - 1, Rr = BB.x + BB.w, T0 = BB.y - 1, B = BB.y + BB.h;
  for (const lx of [BB.x + 7, BB.x + BB.w - 8]) for (let y = B; y <= 96; y++) { p.set(lx, y, '#1a2420'); p.set(lx + 1, y, '#0e1612'); }
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) p.set(x, y, (y === T0 || x === L) ? '#3a4a42' : '#101814');
  for (const lx of [BB.x + 9, BB.x + BB.w - 10]) { p.line(lx, T0, lx, T0 - 3, '#2a3430'); p.line(lx, T0 - 3, lx + 3, T0 - 4, '#2a3430'); p.set(lx + 3, T0 - 3, '#3a4440'); }
  return trim(p);
}
const BB_FLICK = [[71.2, 72.1], [199.4, 200]];        // one lamp stutters twice a loop
function drawBillboard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.sign, BB.x + 23, BB.y + 8, fl ? 0.3 : 0.5);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) { ctx.drawImage(AD_C, BB.x, BB.y); if (fl) { ctx.globalAlpha = 0.28; R(BB.x + BB.w / 2, BB.y, BB.w / 2, BB.h, '#0a0c18'); ctx.globalAlpha = 1; } }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const [lx, on] of [[BB.x + 12, true], [BB.x + BB.w - 7, !fl]]) { dot(lx, BB.y - 4, on ? '#fff6dc' : '#6a6050'); if (on) halo(HL.tiny, lx, BB.y - 3, 0.9); }
}

/* ================= The picked weather ================= */
// The city's own is clear. Rain and snow fall through the towers' light (the drops catch it), on the outfield (far), on
// the infield and the boxes (middle) and past the near rows (near); leaves blow across the open park.
const WEATHER = weatherPlans([]);
const WX_LAND = [[MON_B1 + 2, 168], [168, 214], [H + 4, H + 4]];
const WX_LIGHTS = [...TOWERS.map(T => ({ x: T.x, y: T.top + 46, rx: 64, ry: 60 })), { x: CIT.cx, y: CIT.cy, rx: 12, ry: 14, col: '#ffd8d8' }, { x: 380, y: 70, rx: 30, ry: 16 }];
const WX = { land: WX_LAND, lights: WX_LIGHTS, leaves: { wind: [0, 24, W, 200] } };
const WX_BB = { ...WX, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 8, rx: 26, ry: 14, col: '#ffe8c0' }] };

/* ================= Frame compositor (back to front) ================= */
// pitch: the billboard's face (promo captures, the website); billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick, the city's own (clear) when left out; crowd: the share of the people to show, 0..1 (CROWD
// when left out); 0 = nobody: no players, no fans, the park lit and empty
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  R(0, HY + 8, W, H - HY - 8, '#0a1210');
  drawFar(t, pitch, billboard, crowd);
  drawStand('bleach', t, crowd); drawStand('mon', t, crowd);
  blit(LFROOF_C.back); drawStand('lfg', t, crowd); blit(LFROOF_C.front);
  drawGlare(t, reducedMotion);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  blit(FIELD_C); blit(BFRONT_C); drawPen(t, crowd); blit(WALLS_C);
  drawScoreboard(t, crowd);
  drawGame(t, crowd);
  drawStand('box3', t, crowd); drawStand('box1', t, crowd);
  drawPhones(t, crowd, reducedMotion);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  blit(WALK_C);
  if (crowd > 0) for (const c of CAST) if (here(c.rank, crowd)) drawHero(c, t);
  blit(NEAR_C);
  if (crowd > 0) drawWalkers(t, crowd);
  if (here(0, crowd)) drawVendor(t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'boston-fenway',
  name: 'Boston',
  tagline: 'Night game by the Citgo sign',
  timeZone: 'America/New_York',
  accent: '#44bb11',                                   // the city's accent (the Green Line's green), written out: tools/seo/seo.mjs reads it
  focusX: CIT.cx,
  landmark: [MON.x0 - 4, MON.x1 + 6],                  // the Green Monster, the two light towers on it and the Citgo sign over it
  landmarkTop: TOWERS[0].top,                          // the left light tower's lamps
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'neon', x: CIT.x, y: CIT.y, w: CIT.w, h: CIT.h },
    { kind: 'landmark', x: TOWERS[1].x - TOWERS[1].hw, y: TOWERS[1].top, w: TOWERS[1].hw * 2 + 1, h: TOWERS[1].foot - TOWERS[1].top },
    { kind: 'landmark', x: TOWERS[0].x - TOWERS[0].hw, y: TOWERS[0].top, w: TOWERS[0].hw * 2 + 1, h: TOWERS[0].hh },
  ],
  ambience: { crowd: 0.32, traffic: 0.04, wind: 0.05 },
  events: [
    { t: WAVE.t0 + 0.5, type: 'chime', x: 300, gain: 0.14 },
    { t: SING.peak, type: 'chime', x: 240, gain: 0.16 },
    { t: HR.t + 0.5, type: 'chime', x: 160, gain: 0.22 },
    { t: HR.t + 4, type: 'chime', x: 220, gain: 0.12 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
